import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  useTheme,
  TextField,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ThemeContext } from '../Context/ThemeContext';
import { useSelector } from 'react-redux';
import { resolveFieldsService } from '../apis/EntitiesSelection';
import CheckIcon from '@mui/icons-material/Check';

const BacktrackConfigurationModal = ({
  selectedNode,
  setSelectedNode,
  updateNodeData,
  updatedConfig,
  onConfigUpdate,
  workflowId,
}) => {
  const theme = useTheme();
  const { darkMode } = useContext(ThemeContext);
  const [hydratedOnce, setHydratedOnce] = useState(false);
  const [localResolvedData, setLocalResolvedData] = useState(null); // Store resolved data from direct API call

  // === Redux slices (same shapes used by JoinFunc) ===
  const resolvedDataFromRedux = useSelector(
    (state) => state?.mapping?.resolvedFieldsByWorkflow?.[workflowId],
  ); // array of nodes with { type, fields: { entity_name, fields } }

  // Use local resolved data if available, otherwise fall back to Redux
  const resolvedData = localResolvedData || resolvedDataFromRedux;

  const finalConfigPayload = useSelector(
    (state) => state?.mapping?.finalConfigPayloadByWorkflow?.[workflowId],
  );

  console.log('updatedConfig::', updatedConfig);

  // Helper function to check if a connection is SQL type
  // Checks updatedConfig.nodes for source nodes matching this connection
  // and verifies if their module is "sql"
  const isSqlConnector = (connectionData) => {
    if (!connectionData || !updatedConfig?.nodes) return false;

    const connectionKey = connectionData.connectionKey; // conn_id or compositeKey
    const nodeId = connectionData?.sourceInfo?.node_id;
    const sourceInfo = connectionData?.sourceInfo;

    // Find source nodes in updatedConfig.nodes that match this connection
    const sourceNodes = (updatedConfig.nodes || []).filter((node) => {
      // Must be a source node
      if (node?.type !== 'sourceNode' && node?.type !== 'source') {
        return false;
      }

      // Match by node_id (most reliable)
      if (nodeId && node?.id === nodeId) {
        return true;
      }

      // Match by conn_id
      const nodeConnId =
        node?.config?.connection?.conn_id ||
        node?.data?.config?.connection?.conn_id;
      if (nodeConnId && connectionKey === nodeConnId) {
        return true;
      }

      // Match by compositeKey pattern (nodeId-connId-module)
      if (sourceInfo?.compositeKey && node?.id) {
        const compositeKeyParts = sourceInfo.compositeKey.split('-');
        if (compositeKeyParts[0] === node.id) {
          return true;
        }
      }

      return false;
    });

    // Check if any matching source node has module === "sql"
    return sourceNodes.some((node) => {
      const module = node?.config?.module || node?.data?.config?.module || '';
      return String(module).toLowerCase() === 'sql';
    });
  };

  // ---- Get saved backtrack_entities from node config ----
  const savedBacktrackEntities = useMemo(() => {
    const saved =
      selectedNode?.data?.config?.backtrack_entities ||
      selectedNode?.config?.backtrack_entities ||
      [];
    console.log('BacktrackConfigurationModal - savedBacktrackEntities:', saved);
    return saved;
  }, [selectedNode]);

  // ---- Extract selected source entities from updatedConfig AND saved backtrack_entities ----
  const selectedSourceEntities = useMemo(() => {
    const result = [];
    const seen = new Set(); // Track unique entity+compositeKey combinations

    // 1. First, add entities from saved backtrack_entities (restore from API)
    // This ensures entities that were previously configured are shown even if
    // they're not in the current entitySelections
    if (
      Array.isArray(savedBacktrackEntities) &&
      savedBacktrackEntities.length > 0
    ) {
      savedBacktrackEntities.forEach((btEntity) => {
        const entityName = btEntity?.entity_name;
        const compositeKey = btEntity?.source?.compositeKey;

        if (entityName && compositeKey) {
          const key = `${compositeKey}__${entityName}`;
          if (!seen.has(key)) {
            seen.add(key);
            result.push({
              entity_name: entityName,
              label: btEntity?.label || entityName,
              compositeKey,
              source: btEntity?.source, // Preserve full source info from API
            });
          }
        }
      });
    }

    // 2. Then, add entities from current entitySelections (for new entities)
    // This ensures newly selected entities are also shown
    const srcSelections =
      updatedConfig?.config?.entitySelections?.source ||
      updatedConfig?.entitySelections?.source ||
      {};

    Object.entries(srcSelections).forEach(([compositeKey, arr]) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((e) => {
        const entityName = e?.entity_name;
        if (!entityName) return;

        const key = `${compositeKey}__${entityName}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({
            entity_name: entityName,
            label: e.label || entityName,
            compositeKey,
          });
        }
      });
    });

    console.log(
      'BacktrackConfigurationModal - selectedSourceEntities:',
      result,
    );
    return result;
  }, [updatedConfig, savedBacktrackEntities]);

  useEffect(() => {
    if (
      !Array.isArray(selectedSourceEntities) ||
      selectedSourceEntities.length === 0
    ) {
      return;
    }

    // Build source node configs for field resolution
    const buildSourceConfigs = () => {
      const configs = [];

      // 1. Try to use finalConfigPayload from Redux first
      if (finalConfigPayload && Object.keys(finalConfigPayload).length > 0) {
        const fromPayload = Object.values(finalConfigPayload)
          .filter((n) => n?.type === 'source' || n?.type === 'sourceNode')
          .map((n) => ({
            ...n,
            selectedEntities: selectedSourceEntities.map((e) => ({
              entity_name: e.entity_name,
              label: e.label || e.entity_name,
            })),
          }));
        configs.push(...fromPayload);
      }

      // 2. Also build configs from updatedConfig.nodes for saved entities
      // This ensures entities restored from saved backtrack_entities get their fields resolved
      const sourceNodes = (updatedConfig?.nodes || []).filter(
        (n) => n?.type === 'sourceNode' || n?.type === 'source',
      );

      // Group entities by their source node
      // compositeKey format is typically: "nodeId-connId-module" or similar
      const entitiesByNodeId = new Map();
      const entitiesByConnId = new Map();
      const entitiesByCompositeKey = new Map();

      selectedSourceEntities.forEach((entity) => {
        const source = entity.source || {};
        const compositeKey = entity.compositeKey;

        // Extract nodeId from compositeKey (usually first part before first dash)
        let nodeIdFromKey = null;
        if (compositeKey) {
          const parts = compositeKey.split('-');
          nodeIdFromKey = parts[0]; // First part is usually the node ID
        }

        const nodeId = source.node_id || nodeIdFromKey;
        const connId = source.connection?.conn_id;

        const entityData = {
          entity_name: entity.entity_name,
          label: entity.label || entity.entity_name,
        };

        // Index by nodeId (most reliable)
        if (nodeId) {
          if (!entitiesByNodeId.has(nodeId)) {
            entitiesByNodeId.set(nodeId, []);
          }
          entitiesByNodeId.get(nodeId).push(entityData);
        }

        // Index by connId (fallback)
        if (connId) {
          if (!entitiesByConnId.has(connId)) {
            entitiesByConnId.set(connId, []);
          }
          entitiesByConnId.get(connId).push(entityData);
        }

        // Index by compositeKey (for exact matching)
        if (compositeKey) {
          if (!entitiesByCompositeKey.has(compositeKey)) {
            entitiesByCompositeKey.set(compositeKey, []);
          }
          entitiesByCompositeKey.get(compositeKey).push(entityData);
        }
      });

      // Match source nodes with saved entities
      sourceNodes.forEach((node) => {
        const nodeConnId =
          node?.config?.connection?.conn_id ||
          node?.data?.config?.connection?.conn_id;
        const nodeId = node?.id;
        const nodeModule = node?.config?.module || node?.data?.config?.module;

        // Build compositeKey for this node to match with saved entities
        const nodeCompositeKey =
          nodeId && nodeConnId && nodeModule
            ? `${nodeId}-${nodeConnId}-${nodeModule}`
            : nodeId && nodeConnId
              ? `${nodeId}-${nodeConnId}`
              : null;

        // Find matching entities for this source node
        let matchingEntities = [];

        // Try to match by node_id first (most reliable)
        if (nodeId && entitiesByNodeId.has(nodeId)) {
          matchingEntities = entitiesByNodeId.get(nodeId);
        }
        // Then try by compositeKey (exact match)
        else if (
          nodeCompositeKey &&
          entitiesByCompositeKey.has(nodeCompositeKey)
        ) {
          matchingEntities = entitiesByCompositeKey.get(nodeCompositeKey);
        }
        // Then try by conn_id (fallback)
        else if (nodeConnId && entitiesByConnId.has(nodeConnId)) {
          matchingEntities = entitiesByConnId.get(nodeConnId);
        }
        // Finally, try partial compositeKey match
        else if (nodeId) {
          entitiesByCompositeKey.forEach((entityList, key) => {
            if (key.startsWith(nodeId + '-')) {
              matchingEntities.push(...entityList);
            }
          });
        }

        // If we have matching entities, build a config for this source node
        if (matchingEntities.length > 0) {
          const existingConfig = configs.find((c) => c.id === nodeId);
          if (!existingConfig) {
            configs.push({
              id: nodeId,
              label: node?.config?.label || node?.data?.label,
              connection:
                node?.config?.connection || node?.data?.config?.connection,
              module: node?.config?.module || node?.data?.config?.module,
              sqlMetadata:
                node?.config?.sqlMetadata || node?.data?.config?.sqlMetadata,
              type: 'source',
              selectedEntities: matchingEntities,
            });
          } else {
            // Merge entities if config already exists
            const existingEntities = existingConfig.selectedEntities || [];
            const mergedEntities = [...existingEntities];
            matchingEntities.forEach((entity) => {
              if (
                !mergedEntities.find(
                  (e) => e.entity_name === entity.entity_name,
                )
              ) {
                mergedEntities.push(entity);
              }
            });
            existingConfig.selectedEntities = mergedEntities;
          }
        }
      });

      // 3. If we still don't have configs but have saved entities with source info,
      // build configs directly from saved source info
      if (configs.length === 0 && savedBacktrackEntities.length > 0) {
        const sourceInfoMap = new Map();

        savedBacktrackEntities.forEach((btEntity) => {
          const source = btEntity?.source;
          if (!source) return;

          const sourceKey =
            source.node_id || source.connection?.conn_id || source.compositeKey;
          if (!sourceInfoMap.has(sourceKey)) {
            sourceInfoMap.set(sourceKey, {
              id: source.node_id || `source-${sourceKey}`,
              label: source.connector_name || 'Source',
              connection: source.connection || {
                conn_id: source.connection?.conn_id,
                conn_name: source.connection?.conn_name,
              },
              module: source.module,
              type: 'source',
              selectedEntities: [],
            });
          }

          sourceInfoMap.get(sourceKey).selectedEntities.push({
            entity_name: btEntity.entity_name,
            label: btEntity.label || btEntity.entity_name,
          });
        });

        configs.push(...Array.from(sourceInfoMap.values()));
      }

      return configs;
    };

    const configsToResolve = buildSourceConfigs();

    if (configsToResolve.length === 0) {
      console.warn(
        'BacktrackConfigurationModal - No source configs to resolve fields for',
      );
      return;
    }

    (async () => {
      try {
        const resolvedFields = await resolveFieldsService({
          label: 'FieldResolution',
          configs: configsToResolve,
        });
        console.log(
          'BacktrackConfigurationModal - Fields resolved successfully',
          resolvedFields,
        );

        // Store the resolved data locally so fieldsByEntity can use it immediately
        // The structure is an array: [{node_id, type, entity, fields: {...}}, ...]
        if (Array.isArray(resolvedFields) && resolvedFields.length > 0) {
          setLocalResolvedData(resolvedFields);
        }
      } catch (err) {
        console.warn('Backtrack resolveFieldsService failed:', err);
      }
    })();
  }, [
    finalConfigPayload,
    selectedSourceEntities,
    updatedConfig,
    savedBacktrackEntities,
    workflowId,
  ]);

  // ---- Build fieldsByEntity from resolvedData (array), fallback to config.mappings ----
  const fieldsByEntity = useMemo(() => {
    const out = {};
    const pushField = (entity, name, type, isPrimaryKey) => {
      if (!entity || !name) return;
      // Normalize entity name for consistent matching
      const normalizedEntity = String(entity).toLowerCase();
      if (!out[normalizedEntity]) out[normalizedEntity] = [];
      if (!out[normalizedEntity].some((f) => f.name === name)) {
        out[normalizedEntity].push({
          name,
          type: (type || '').toLowerCase() || undefined,
          is_primary_key: isPrimaryKey || false,
        });
      }
    };

    const selectedSet = new Set(
      (selectedSourceEntities || []).map((e) =>
        String(e.entity_name).toLowerCase(),
      ),
    );

    let dataArray = null;
    if (Array.isArray(resolvedData)) {
      dataArray = resolvedData;
      console.log(
        'BacktrackConfigurationModal - resolvedData is array:',
        dataArray.length,
        'items',
      );
    } else if (resolvedData?.fields && Array.isArray(resolvedData.fields)) {
      dataArray = resolvedData.fields;
      console.log(
        'BacktrackConfigurationModal - resolvedData has fields property:',
        dataArray.length,
        'items',
      );
    } else if (
      resolvedData?.data?.fields &&
      Array.isArray(resolvedData.data.fields)
    ) {
      dataArray = resolvedData.data.fields;
      console.log(
        'BacktrackConfigurationModal - resolvedData.data has fields property:',
        dataArray.length,
        'items',
      );
    } else {
      console.warn(
        'BacktrackConfigurationModal - resolvedData structure not recognized:',
        resolvedData,
      );
    }

    // A) Primary: from resolvedData array (like JoinFunc does)
    if (dataArray && dataArray.length > 0) {
      dataArray
        .filter(
          (node) => node?.type === 'source' || node?.type === 'sourceNode',
        )
        .forEach((node) => {
          // Try multiple possible structures for entity name
          const entityName =
            node?.entity ||
            node?.fields?.entity_name ||
            node?.fields?.entity ||
            node?.entity_name;

          if (!entityName) {
            console.warn(
              'BacktrackConfigurationModal - No entity name found in node:',
              node,
            );
            return;
          }

          const normalizedEntityName = String(entityName).toLowerCase();
          if (!selectedSet.has(normalizedEntityName)) {
            console.log(
              `BacktrackConfigurationModal - Entity ${entityName} not in selectedSet, skipping`,
            );
            return;
          }

          // Try multiple possible structures for fields
          // Based on the structure: {entity: 'Account', fields: {fields: [...]}}
          let nodeFields = [];
          if (node?.fields) {
            if (Array.isArray(node.fields)) {
              nodeFields = node.fields;
            } else if (Array.isArray(node.fields.fields)) {
              nodeFields = node.fields.fields;
            } else if (
              node.fields.fields &&
              typeof node.fields.fields === 'object'
            ) {
              // If fields.fields is an object, try to extract array from it
              // Check if it's an object with array values or a single object
              if (Array.isArray(Object.values(node.fields.fields)[0])) {
                nodeFields = Object.values(node.fields.fields).flat();
              } else {
                // If fields.fields is a single object, try to convert it to array
                const fieldsObj = node.fields.fields;
                if (fieldsObj && typeof fieldsObj === 'object') {
                  // Check if it has a 'fields' property that's an array
                  if (Array.isArray(fieldsObj.fields)) {
                    nodeFields = fieldsObj.fields;
                  } else {
                    // Try to extract fields from the object structure
                    nodeFields = Object.entries(fieldsObj).map(
                      ([key, value]) => {
                        if (typeof value === 'object' && value !== null) {
                          return {
                            ...value,
                            field_name: value.field_name || key,
                          };
                        }
                        return { field_name: key, ...value };
                      },
                    );
                  }
                }
              }
            }
          }

          // If still no fields, log the structure for debugging
          if (nodeFields.length === 0 && node?.fields) {
            console.warn(
              `BacktrackConfigurationModal - Could not extract fields from node.fields:`,
              node.fields,
            );
          }

          console.log(
            `BacktrackConfigurationModal - Processing entity ${entityName} with ${nodeFields.length} fields`,
            { node, nodeFields },
          );

          nodeFields.forEach((f) => {
            const fname =
              f.field_name ||
              f.field_label ||
              f.apiName ||
              f.name ||
              f.column ||
              f.fieldName;
            const ftype =
              f.data_type ||
              f.type ||
              f.dataType ||
              f.fieldType ||
              f.Type ||
              f.datatype;
            const isPrimaryKey =
              f.is_primary_key === true ||
              f.isPrimaryKey === true ||
              f.is_primary === true ||
              f.isPrimary === true ||
              false;

            if (fname) {
              pushField(entityName, fname, ftype, isPrimaryKey);
            }
          });
        });
    } else {
      console.warn(
        'BacktrackConfigurationModal - resolvedData is not in expected format:',
        resolvedData,
      );
    }

    // B) Fallback: infer from mappings in updatedConfig when resolver not ready
    const cfgMappings =
      updatedConfig?.config?.mappings || updatedConfig?.mappings || [];
    cfgMappings.forEach((m) => {
      const srcKey = m?.sourceEntity;
      const entityName = srcKey?.includes('__')
        ? srcKey.split('__').pop()
        : srcKey;
      if (entityName) {
        (m?.mappings || []).forEach((row) => {
          if (row?.sourceField) {
            pushField(entityName, row.sourceField, undefined, false);
          }
        });
      }
    });

    // Sort each entity's fields
    Object.keys(out).forEach((k) =>
      out[k].sort((a, b) => a.name.localeCompare(b.name)),
    );

    console.log('BacktrackConfigurationModal - fieldsByEntity result:', out);
    return out;
  }, [resolvedData, updatedConfig, selectedSourceEntities]);

  // ---- UI state for selections per entity ----
  // Structure: { [configId]: { connectionKey, entityKey, status_update, success_value, failure_value, raw_response_field, identifier_update } }
  const [entitySelections, setEntitySelections] = useState(() => {
    const existing =
      selectedNode?.data?.config?.entity_field_selections ||
      selectedNode?.config?.entity_field_selections ||
      {};
    return { ...existing };
  });

  // Track configuration IDs for multiple configs per source/entity
  const [configIds, setConfigIds] = useState(() => {
    // Initialize with at least one config per entity
    const ids = [];
    selectedSourceEntities.forEach(({ compositeKey, entity_name }) => {
      const rowKey = `${compositeKey}__${entity_name}`;
      ids.push(rowKey);
    });
    return ids;
  });

  // Track selected entity per connection for the configuration form
  const [selectedEntityPerConnection, setSelectedEntityPerConnection] =
    useState({});

  // Track new configuration blocks being added (temporary blocks for adding new configs)
  // Structure: { [connectionKey]: { configId, entityKey } | null }
  const [newConfigBlocks, setNewConfigBlocks] = useState({});
  // Track which source blocks are hidden/deleted (only for multiple sources)
  const [hiddenSourceBlocks, setHiddenSourceBlocks] = useState(new Set());

  // Helper function to build entity to source info mapping (moved before use)
  const buildEntityToSourceInfo = (cfg) => {
    const out = {}; // entity_name → array of source info
    const srcSelections =
      cfg?.config?.entitySelections?.source ||
      cfg?.entitySelections?.source ||
      {};

    const srcNodes = (cfg?.nodes || []).filter(
      (n) => n?.type === 'sourceNode' || n?.type === 'source',
    );

    Object.entries(srcSelections).forEach(([compositeKey, entities]) => {
      if (!Array.isArray(entities)) return;

      // Try exact prefix match e.g. "Salesforce-123-connid-module"
      let matchedNode =
        srcNodes.find((n) => compositeKey.startsWith(n.id + '-')) ||
        // fallback: match by conn_id inside composite key
        srcNodes.find((n) =>
          compositeKey.includes(
            n?.config?.connection?.conn_id || '___NO_MATCH___',
          ),
        ) ||
        null;

      const conn = matchedNode?.config?.connection || {};

      entities.forEach((e) => {
        const name = e?.entity_name;
        if (!name) return;

        if (!out[name]) out[name] = [];

        out[name].push({
          compositeKey,
          node_id: matchedNode?.id || null,
          connector_name: conn?.conn_name || null,
          environment: conn?.environment || null,
          connection: {
            conn_id: conn?.conn_id || null,
            conn_name: conn?.conn_name || null,
          },
        });
      });
    });

    return out;
  };

  // Group entities by connection
  const entitiesByConnection = useMemo(() => {
    const grouped = {};
    selectedSourceEntities.forEach((entity) => {
      const sourceInfo =
        entity.source ||
        buildEntityToSourceInfo(updatedConfig)?.[entity.entity_name]?.find(
          (s) => s.compositeKey === entity.compositeKey,
        );
      const connectionName =
        sourceInfo?.connection?.conn_name ||
        sourceInfo?.connector_name ||
        'Unknown';
      const connectionKey =
        sourceInfo?.connection?.conn_id ||
        sourceInfo?.compositeKey ||
        entity.compositeKey;

      if (!grouped[connectionKey]) {
        grouped[connectionKey] = {
          connectionName,
          connectionKey,
          sourceInfo,
          entities: [],
        };
      }
      grouped[connectionKey].entities.push(entity);
    });
    return grouped;
  }, [selectedSourceEntities, updatedConfig]);

  // Initialize selected entity per connection
  useEffect(() => {
    if (Object.keys(entitiesByConnection).length > 0) {
      setSelectedEntityPerConnection((prev) => {
        const updated = { ...prev };
        Object.entries(entitiesByConnection).forEach(([connKey, connData]) => {
          // Only set if not already set
          if (!updated[connKey] && connData.entities.length > 0) {
            const firstEntity = connData.entities[0];
            updated[connKey] =
              `${firstEntity.compositeKey}__${firstEntity.entity_name}`;
          }
        });
        return updated;
      });
    }
  }, [entitiesByConnection]);

  useEffect(() => {
    if (selectedNode) {
      setHydratedOnce(false);
    }
  }, [selectedNode?.id]);

  useEffect(() => {
    if (!selectedNode) return;
    if (hydratedOnce) return; // prevent overwriting user selections

    const restored = {};

    // 1️⃣ restore saved values from backend (backtrack_entities)
    const savedRows =
      selectedNode?.data?.config?.backtrack_entities ||
      selectedNode?.config?.backtrack_entities ||
      [];

    console.log(
      'BacktrackConfigurationModal - Restoring from savedRows:',
      savedRows,
    );

    savedRows.forEach((row) => {
      const compositeKey = row?.source?.compositeKey;
      const entityName = row?.entity_name;

      if (!compositeKey || !entityName) {
        console.warn(
          'BacktrackConfigurationModal - Invalid row (missing compositeKey or entity_name):',
          row,
        );
        return;
      }

      const rowKey = `${compositeKey}__${entityName}`;

      restored[rowKey] = {
        status_update: row?.status_update?.field || '',
        success_value:
          row?.status_update?.success_value !== undefined
            ? row.status_update.success_value
            : '',
        failure_value:
          row?.status_update?.failure_value !== undefined
            ? row.status_update.failure_value
            : '',
        raw_response_field: row?.raw_response_field?.field || '',
        identifier_update: row?.identifier_update?.field || '',
        primary_key: row?.primary_key?.field || '',
      };
    });

    // 2️⃣ ensure all selected entities have a rowKey (from both saved and current)
    const ensured = {};
    const ids = [];
    selectedSourceEntities.forEach(
      ({ compositeKey, entity_name, source: savedSource }) => {
        const rowKey = `${compositeKey}__${entity_name}`;
        ids.push(rowKey);

        // Get connectionKey for this entity
        const sourceInfo =
          savedSource ||
          buildEntityToSourceInfo(updatedConfig)?.[entity_name]?.find(
            (s) => s.compositeKey === compositeKey,
          );
        const connectionKey =
          sourceInfo?.connection?.conn_id ||
          sourceInfo?.compositeKey ||
          compositeKey;

        ensured[rowKey] = {
          ...(restored[rowKey] || {
            status_update: '',
            success_value: '',
            failure_value: '',
            raw_response_field: '',
            identifier_update: '',
            primary_key: '',
          }),
          connectionKey,
          entityKey: rowKey,
        };
      },
    );

    console.log(
      'BacktrackConfigurationModal - Restored entitySelections:',
      ensured,
    );

    // 3️⃣ set final merged state
    setEntitySelections(ensured);
    setConfigIds(ids);

    // 4️⃣ Set selected entity per connection based on saved data
    setSelectedEntityPerConnection((prev) => {
      const updated = { ...prev };
      Object.entries(entitiesByConnection).forEach(([connKey, connData]) => {
        // Find first entity that has a saved configuration
        const entityWithConfig = connData.entities.find((entity) => {
          const entityKey = `${entity.compositeKey}__${entity.entity_name}`;
          return ids.includes(entityKey) || ensured[entityKey];
        });
        if (entityWithConfig && !updated[connKey]) {
          updated[connKey] =
            `${entityWithConfig.compositeKey}__${entityWithConfig.entity_name}`;
        } else if (!updated[connKey] && connData.entities.length > 0) {
          // Default to first entity if no saved config
          const firstEntity = connData.entities[0];
          updated[connKey] =
            `${firstEntity.compositeKey}__${firstEntity.entity_name}`;
        }
      });
      return updated;
    });

    // 5️⃣ IMPORTANT: mark hydrated so UI updates won't be overridden
    setHydratedOnce(true);
  }, [
    selectedNode,
    selectedSourceEntities,
    hydratedOnce,
    entitiesByConnection,
  ]);

  const setFor = (configId, key, value) => {
    setEntitySelections((prev) => ({
      ...prev,
      [configId]: { ...(prev[configId] || {}), [key]: value },
    }));
  };

  // Helper function to get configured entities for a connection
  const getConfiguredEntities = (connectionKey, entities) => {
    const configuredEntityKeys = new Set();

    // Check all configIds and entitySelections
    configIds.forEach((id) => {
      const config = entitySelections[id];
      if (config?.entityKey && config?.connectionKey === connectionKey) {
        // Only count as configured if it has status_update (required field)
        if (config.status_update) {
          configuredEntityKeys.add(config.entityKey);
        }
      }
      // Also check if configId itself matches an entityKey pattern
      const parts = id.split('__');
      if (parts.length >= 2) {
        const possibleEntityKey = `${parts[0]}__${parts[1]}`;
        // Check if this matches any entity in this connection
        const matchesEntity = entities.some(
          (e) => `${e.compositeKey}__${e.entity_name}` === possibleEntityKey,
        );
        if (matchesEntity) {
          const config = entitySelections[id];
          if (config?.status_update) {
            configuredEntityKeys.add(possibleEntityKey);
          }
        }
      }
    });

    // Also check entitySelections directly for entityKeys
    Object.values(entitySelections).forEach((config) => {
      if (
        config?.entityKey &&
        config?.connectionKey === connectionKey &&
        config?.status_update
      ) {
        configuredEntityKeys.add(config.entityKey);
      }
    });

    // Filter to get configured entities
    return entities.filter((e) => {
      const entityKey = `${e.compositeKey}__${e.entity_name}`;
      return configuredEntityKeys.has(entityKey);
    });
  };

  // Helper function to get unconfigured entities for a connection
  const getUnconfiguredEntities = (connectionKey, entities) => {
    const configuredEntityKeys = new Set();

    // Check all configIds and entitySelections
    configIds.forEach((id) => {
      const config = entitySelections[id];
      if (config?.entityKey && config?.connectionKey === connectionKey) {
        configuredEntityKeys.add(config.entityKey);
      }
      // Also check if configId itself matches an entityKey pattern
      const parts = id.split('__');
      if (parts.length >= 2) {
        const possibleEntityKey = `${parts[0]}__${parts[1]}`;
        // Check if this matches any entity in this connection
        const matchesEntity = entities.some(
          (e) => `${e.compositeKey}__${e.entity_name}` === possibleEntityKey,
        );
        if (matchesEntity) {
          configuredEntityKeys.add(possibleEntityKey);
        }
      }
    });

    // Also check entitySelections directly for entityKeys
    Object.values(entitySelections).forEach((config) => {
      if (config?.entityKey && config?.connectionKey === connectionKey) {
        configuredEntityKeys.add(config.entityKey);
      }
    });

    // Exclude entities that are in newConfigBlocks for this connection (being configured)
    if (newConfigBlocks[connectionKey]) {
      const newBlock = newConfigBlocks[connectionKey];
      if (newBlock.entityKey) {
        configuredEntityKeys.add(newBlock.entityKey);
      }
    }

    // Filter out configured entities
    return entities.filter((e) => {
      const entityKey = `${e.compositeKey}__${e.entity_name}`;
      return !configuredEntityKeys.has(entityKey);
    });
  };

  // Initialize entitySelections with connectionKey and entityKey for existing configs
  useEffect(() => {
    if (configIds.length > 0) {
      setEntitySelections((prev) => {
        const updated = { ...prev };
        configIds.forEach((configId) => {
          if (updated[configId] && !updated[configId].entityKey) {
            // Extract entityKey from configId
            const parts = configId.split('__');
            if (parts.length >= 2) {
              const entityKey = `${parts[0]}__${parts[1]}`;
              const entity = selectedSourceEntities.find(
                (e) => `${e.compositeKey}__${e.entity_name}` === entityKey,
              );
              if (entity) {
                const sourceInfo =
                  entity.source ||
                  buildEntityToSourceInfo(updatedConfig)?.[
                    entity.entity_name
                  ]?.find((s) => s.compositeKey === entity.compositeKey);
                const connectionKey =
                  sourceInfo?.connection?.conn_id ||
                  sourceInfo?.compositeKey ||
                  entity.compositeKey;
                updated[configId] = {
                  ...updated[configId],
                  connectionKey,
                  entityKey,
                };
              }
            }
          }
        });
        return updated;
      });
    }
  }, [configIds, selectedSourceEntities, updatedConfig]);

  const removeConfiguration = (configId) => {
    setConfigIds((prev) => prev.filter((id) => id !== configId));
    setEntitySelections((prev) => {
      const updated = { ...prev };
      delete updated[configId];
      return updated;
    });
    // Clear validation errors for this config
    setValidationErrors((prev) => {
      const updated = { ...prev };
      delete updated[configId];
      return updated;
    });
  };

  // Remove entire source block and all its configurations
  const removeSourceBlock = (connectionKey) => {
    // Remove all configurations for this source
    setConfigIds((prev) => {
      return prev.filter((id) => {
        const config = entitySelections[id];
        return config?.connectionKey !== connectionKey;
      });
    });
    setEntitySelections((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((id) => {
        if (updated[id]?.connectionKey === connectionKey) {
          delete updated[id];
        }
      });
      return updated;
    });
    // Clear validation errors for this source
    setValidationErrors((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((id) => {
        const config = entitySelections[id];
        if (config?.connectionKey === connectionKey) {
          delete updated[id];
        }
      });
      return updated;
    });
    // Remove from newConfigBlocks if exists
    setNewConfigBlocks((prev) => {
      const updated = { ...prev };
      delete updated[connectionKey];
      return updated;
    });
    // Remove from selectedEntityPerConnection
    setSelectedEntityPerConnection((prev) => {
      const updated = { ...prev };
      delete updated[connectionKey];
      return updated;
    });
    // Hide the source block
    setHiddenSourceBlocks((prev) => new Set([...prev, connectionKey]));
  };

  // Restore a hidden source block
  const restoreSourceBlock = (connectionKey) => {
    setHiddenSourceBlocks((prev) => {
      const updated = new Set(prev);
      updated.delete(connectionKey);
      return updated;
    });
  };

  // Validation errors state: { [configId]: { status_update, success_value, failure_value, identifier_update } }
  const [validationErrors, setValidationErrors] = useState({});

  // Validate a single configuration
  const validateConfig = (configId, config) => {
    const errors = {};

    // Status update is required
    if (!config.status_update || config.status_update.trim() === '') {
      errors.status_update = 'Status update field is required';
    }

    // If status update is selected, success and failure values are required
    if (config.status_update && config.status_update.trim() !== '') {
      if (!config.success_value || config.success_value.trim() === '') {
        errors.success_value =
          'Success value is required when status update is selected';
      }
      if (!config.failure_value || config.failure_value.trim() === '') {
        errors.failure_value =
          'Failure value is required when status update is selected';
      }
    }

    // Identifier update is required
    if (!config.identifier_update || config.identifier_update.trim() === '') {
      errors.identifier_update = 'Identifier update field is required';
    }

    return errors;
  };

  const getDropdownStyle = (hasError = false) => ({
    mb: 1,
    borderRadius: '6px',
    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
    height: '2.4rem',
    width: '100%',
    color: darkMode ? '#ffffff' : theme.palette.text.primary,
    '.MuiSelect-select': {
      color: darkMode ? '#ffffff' : theme.palette.text.primary,
      padding: '8px 14px',
      fontSize: '0.9rem',
      width: '100%',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : darkMode ? '#404040' : '#DDDFE3',
      borderWidth: '1px',
      borderRadius: '6px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : '#2196F3',
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : '#2196F3',
      borderWidth: '1px',
      boxShadow: hasError
        ? '0 0 0 3px rgba(244, 67, 54, 0.1)'
        : '0 0 0 3px rgba(33, 150, 243, 0.1)',
    },
    '& .MuiSvgIcon-root': {
      color: darkMode ? '#ffffff' : '#666666',
    },
    boxShadow: darkMode
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
  });

  const getTextFieldStyle = (hasError = false) => ({
    mb: 1,
    mt: 1,
    borderRadius: '6px',
    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
    width: '100%',
    '& .MuiOutlinedInput-root': {
      color: darkMode ? '#ffffff' : theme.palette.text.primary,
      fontSize: '0.9rem',
      height: '2.8rem',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: hasError ? '#f44336' : darkMode ? '#404040' : '#DDDFE3',
        borderWidth: '1px',
        borderRadius: '6px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: hasError ? '#f44336' : '#2196F3',
        borderWidth: '1px',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: hasError ? '#f44336' : '#2196F3',
        borderWidth: '1px',
        boxShadow: hasError
          ? '0 0 0 3px rgba(244, 67, 54, 0.1)'
          : '0 0 0 3px rgba(33, 150, 243, 0.1)',
      },
    },
    '& .MuiInputLabel-root': {
      color: darkMode ? '#ffffff' : theme.palette.text.primary,
    },
    boxShadow: darkMode
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
  });

  const MENU_PROPS = {
    PaperProps: {
      sx: {
        backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
        color: darkMode ? '#ffffff' : theme.palette.text.primary,
        boxShadow: darkMode
          ? '0 8px 32px rgba(0, 0, 0, 0.4)'
          : '0 8px 32px rgba(0, 0, 0, 0.12)',
        mt: 1,
        borderRadius: '12px',
        border: darkMode ? '1px solid #404040' : '1px solid #e0e0e0',
        maxHeight: '300px',
        minWidth: '320px',
        '& .MuiMenuItem-root': {
          mx: 1,
          my: 0.5,
          borderRadius: '8px',
          fontSize: '0.95rem',
          fontWeight: 400,
          padding: '12px 16px',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          '&.Mui-selected': {
            backgroundColor: darkMode ? '#1976D2' : '#e3f2fd',
            color: darkMode ? '#ffffff' : '#1976D2',
            fontWeight: 500,
            '&:hover': { backgroundColor: darkMode ? '#1565C0' : '#bbdefb' },
          },
          '&:hover': { backgroundColor: darkMode ? '#404040' : '#f5f5f5' },
        },
      },
    },
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  };

  // --- helpers ---
  const getSourceSelections = (cfg) =>
    cfg?.config?.entitySelections?.source ||
    cfg?.entitySelections?.source ||
    {};

  console.log('backtrack getSourceSelections:', getSourceSelections);

  const handleSave = () => {
    // Collect only visible configIds (configs that are actually shown in UI)
    const visibleConfigIds = new Set();

    // Iterate through all connections and collect visible configs (exclude hidden sources)
    Object.entries(entitiesByConnection)
      .filter(([connectionKey]) => !hiddenSourceBlocks.has(connectionKey))
      .forEach(([connectionKey, connectionData]) => {
        const entities = connectionData.entities;
        const selectedEntityKey =
          selectedEntityPerConnection[connectionKey] ||
          (entities.length === 1
            ? `${entities[0].compositeKey}__${entities[0].entity_name}`
            : null);

        if (selectedEntityKey) {
          const configsForEntity = configIds.filter((id) => {
            // Skip configs from hidden sources
            const config = entitySelections[id];
            if (
              config?.connectionKey &&
              hiddenSourceBlocks.has(config.connectionKey)
            ) {
              return false;
            }
            if (id.startsWith(selectedEntityKey)) return true;
            return config?.entityKey === selectedEntityKey;
          });

          // When multiple entities exist, only validate the first visible config
          // When single entity, validate all configs
          const configsToValidate =
            entities.length > 1
              ? configsForEntity.slice(0, 1)
              : configsForEntity;

          configsToValidate.forEach((configId) => {
            visibleConfigIds.add(configId);
          });

          // Also include selectedEntityKey if it exists in entitySelections (for empty form case)
          if (
            entitySelections[selectedEntityKey] &&
            !visibleConfigIds.has(selectedEntityKey)
          ) {
            visibleConfigIds.add(selectedEntityKey);
          }
        }
      });

    // Check if at least one source is configured (not all are hidden)
    const totalSources = Object.keys(entitiesByConnection).length;
    const visibleSources = totalSources - hiddenSourceBlocks.size;

    if (visibleSources === 0) {
      alert(
        'At least one source must be configured. Please restore a source block or configure at least one source.',
      );
      return;
    }

    // Validate only visible configurations
    const allErrors = {};
    let hasErrors = false;

    visibleConfigIds.forEach((configId) => {
      const config = entitySelections[configId] || {};
      const errors = validateConfig(configId, config);
      if (Object.keys(errors).length > 0) {
        allErrors[configId] = errors;
        hasErrors = true;
      }
    });

    // Check if at least one source has valid configurations
    const visibleSourceKeys = Object.keys(entitiesByConnection).filter(
      (key) => !hiddenSourceBlocks.has(key),
    );
    let hasAtLeastOneValidConfig = false;

    visibleSourceKeys.forEach((connectionKey) => {
      const connectionData = entitiesByConnection[connectionKey];
      const entities = connectionData.entities;
      const configuredEntities = getConfiguredEntities(connectionKey, entities);
      if (configuredEntities.length > 0) {
        hasAtLeastOneValidConfig = true;
      }
    });

    if (!hasAtLeastOneValidConfig) {
      alert(
        'At least one source must have a valid configuration. Please configure at least one entity for one source.',
      );
      return;
    }

    // If there are validation errors, set them and return
    if (hasErrors) {
      setValidationErrors(allErrors);
      // Scroll to first error
      const firstErrorId = Array.from(visibleConfigIds)[0];
      const errorElement = document.querySelector(
        `[data-config-id="${firstErrorId}"]`,
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrors({});

    const entityToSource = buildEntityToSourceInfo(updatedConfig);

    // fast lookup: entity -> field -> type
    // Note: fieldsByEntity uses normalized (lowercase) entity names as keys
    const fieldTypeByEntity = {};
    Object.entries(fieldsByEntity || {}).forEach(
      ([normalizedEntity, fields]) => {
        // Store with both normalized and original entity name for lookup
        fieldTypeByEntity[normalizedEntity] = {};
        (fields || []).forEach((f) => {
          const name = f?.name;
          if (name) {
            const fieldType = String(f?.type || '').toLowerCase() || null;
            fieldTypeByEntity[normalizedEntity][name] = fieldType;
          }
        });
      },
    );

    // Build backtrack_entities from all configurations
    const backtrack_entities = [];

    // Group configs by entity (exclude configs from hidden sources)
    const configsByEntity = {};
    configIds.forEach((configId) => {
      const config = entitySelections[configId] || {};

      // Skip configurations from hidden source blocks
      if (
        config.connectionKey &&
        hiddenSourceBlocks.has(config.connectionKey)
      ) {
        return;
      }

      if (!config.entityKey) {
        // Extract entityKey from configId if not set
        const parts = configId.split('__');
        if (parts.length >= 2) {
          config.entityKey = `${parts[0]}__${parts[1]}`;
        }
      }
      const entityKey = config.entityKey || configId;
      if (!configsByEntity[entityKey]) {
        configsByEntity[entityKey] = [];
      }
      configsByEntity[entityKey].push({ configId, config });
    });

    // Process each entity's configurations
    Object.entries(configsByEntity).forEach(([entityKey, configs]) => {
      const [compositeKey, entityName] = entityKey.split('__');
      const entity = selectedSourceEntities.find(
        (e) =>
          `${e.compositeKey}__${e.entity_name}` === entityKey ||
          e.entity_name === entityName,
      );

      if (!entity) return;

      const { entity_name, label, source: savedSource } = entity;

      // For each configuration, create a backtrack entity entry
      configs.forEach(({ config }) => {
        let sourceInfo = (entityToSource[entity_name] || []).find(
          (src) => src.compositeKey === compositeKey,
        );

        // If not found in entityToSource, use saved source info
        if (!sourceInfo && savedSource) {
          sourceInfo = {
            compositeKey: savedSource.compositeKey || compositeKey,
            node_id: savedSource.node_id || null,
            connector_name: savedSource.connector_name || null,
            environment: savedSource.environment || null,
            connection: savedSource.connection || {
              conn_id: savedSource.connection?.conn_id || null,
              conn_name: savedSource.connection?.conn_name || null,
            },
          };
        }

        // If still not found, try to build from updatedConfig nodes
        if (!sourceInfo) {
          const sourceNodes = (updatedConfig?.nodes || []).filter(
            (n) => n?.type === 'sourceNode' || n?.type === 'source',
          );
          const matchedNode = sourceNodes.find((n) => {
            const nodeId = n?.id;
            const nodeConnId =
              n?.config?.connection?.conn_id ||
              n?.data?.config?.connection?.conn_id;
            const nodeModule = n?.config?.module || n?.data?.config?.module;
            const nodeCompositeKey =
              nodeId && nodeConnId && nodeModule
                ? `${nodeId}-${nodeConnId}-${nodeModule}`
                : nodeId && nodeConnId
                  ? `${nodeId}-${nodeConnId}`
                  : null;
            return (
              nodeCompositeKey === compositeKey ||
              nodeId === compositeKey.split('-')[0]
            );
          });

          if (matchedNode) {
            const conn =
              matchedNode?.config?.connection ||
              matchedNode?.data?.config?.connection ||
              {};
            sourceInfo = {
              compositeKey,
              node_id: matchedNode.id,
              connector_name: conn?.conn_name || null,
              environment: conn?.environment || null,
              connection: {
                conn_id: conn?.conn_id || null,
                conn_name: conn?.conn_name || null,
              },
            };
          }
        }

        const sField = config.status_update || null;
        const iField = config.identifier_update || null;
        const rField = config.raw_response_field || null;
        const tField = config.primary_key || null;
        const successValue = config.success_value || null;
        const failureValue = config.failure_value || null;

        // Normalize entity name for lookup in fieldTypeByEntity
        const normalizedEntityName = String(entity_name).toLowerCase();

        // Get field types - try both normalized and original entity name
        let sFieldType =
          fieldTypeByEntity[normalizedEntityName]?.[sField] ||
          fieldTypeByEntity[entity_name]?.[sField] ||
          null;
        let iFieldType =
          fieldTypeByEntity[normalizedEntityName]?.[iField] ||
          fieldTypeByEntity[entity_name]?.[iField] ||
          null;
        let rFieldType =
          fieldTypeByEntity[normalizedEntityName]?.[rField] ||
          fieldTypeByEntity[entity_name]?.[rField] ||
          null;
        let tFieldType =
          fieldTypeByEntity[normalizedEntityName]?.[tField] ||
          fieldTypeByEntity[entity_name]?.[tField] ||
          null;

        // Fallback: Try to get field type from resolvedData directly
        if (!sFieldType && sField && resolvedData) {
          const dataArray = Array.isArray(resolvedData)
            ? resolvedData
            : resolvedData?.fields || [];
          const entityData = dataArray.find(
            (node) =>
              (node?.type === 'source' || node?.type === 'sourceNode') &&
              (String(node?.entity || '').toLowerCase() ===
                normalizedEntityName ||
                String(node?.fields?.entity_name || '').toLowerCase() ===
                  normalizedEntityName ||
                String(node?.fields?.entity || '').toLowerCase() ===
                  normalizedEntityName),
          );

          if (entityData) {
            const nodeFields =
              entityData?.fields?.fields || entityData?.fields || [];
            const field = Array.isArray(nodeFields)
              ? nodeFields.find(
                  (f) =>
                    (
                      f.field_name ||
                      f.name ||
                      f.fieldName ||
                      ''
                    ).toLowerCase() === String(sField).toLowerCase(),
                )
              : null;
            if (field) {
              sFieldType =
                String(
                  field.data_type ||
                    field.type ||
                    field.dataType ||
                    field.fieldType ||
                    field.datatype ||
                    '',
                ).toLowerCase() || null;
            }
          }
        }

        if (!iFieldType && iField && resolvedData) {
          const dataArray = Array.isArray(resolvedData)
            ? resolvedData
            : resolvedData?.fields || [];
          const entityData = dataArray.find(
            (node) =>
              (node?.type === 'source' || node?.type === 'sourceNode') &&
              (String(node?.entity || '').toLowerCase() ===
                normalizedEntityName ||
                String(node?.fields?.entity_name || '').toLowerCase() ===
                  normalizedEntityName ||
                String(node?.fields?.entity || '').toLowerCase() ===
                  normalizedEntityName),
          );

          if (entityData) {
            const nodeFields =
              entityData?.fields?.fields || entityData?.fields || [];
            const field = Array.isArray(nodeFields)
              ? nodeFields.find(
                  (f) =>
                    (
                      f.field_name ||
                      f.name ||
                      f.fieldName ||
                      ''
                    ).toLowerCase() === String(iField).toLowerCase(),
                )
              : null;
            if (field) {
              iFieldType =
                String(
                  field.data_type ||
                    field.type ||
                    field.dataType ||
                    field.fieldType ||
                    field.datatype ||
                    '',
                ).toLowerCase() || null;
            }
          }
        }

        if (!rFieldType && rField && resolvedData) {
          const dataArray = Array.isArray(resolvedData)
            ? resolvedData
            : resolvedData?.fields || [];
          const entityData = dataArray.find(
            (node) =>
              (node?.type === 'source' || node?.type === 'sourceNode') &&
              (String(node?.entity || '').toLowerCase() ===
                normalizedEntityName ||
                String(node?.fields?.entity_name || '').toLowerCase() ===
                  normalizedEntityName ||
                String(node?.fields?.entity || '').toLowerCase() ===
                  normalizedEntityName),
          );

          if (entityData) {
            const nodeFields =
              entityData?.fields?.fields || entityData?.fields || [];
            const field = Array.isArray(nodeFields)
              ? nodeFields.find(
                  (f) =>
                    (
                      f.field_name ||
                      f.name ||
                      f.fieldName ||
                      ''
                    ).toLowerCase() === String(rField).toLowerCase(),
                )
              : null;
            if (field) {
              rFieldType =
                String(
                  field.data_type ||
                    field.type ||
                    field.dataType ||
                    field.fieldType ||
                    field.datatype ||
                    '',
                ).toLowerCase() || null;
            }
          }
        }

        if (!tFieldType && tField && resolvedData) {
          const dataArray = Array.isArray(resolvedData)
            ? resolvedData
            : resolvedData?.fields || [];
          const entityData = dataArray.find(
            (node) =>
              (node?.type === 'source' || node?.type === 'sourceNode') &&
              (String(node?.entity || '').toLowerCase() ===
                normalizedEntityName ||
                String(node?.fields?.entity_name || '').toLowerCase() ===
                  normalizedEntityName ||
                String(node?.fields?.entity || '').toLowerCase() ===
                  normalizedEntityName),
          );

          if (entityData) {
            const nodeFields =
              entityData?.fields?.fields || entityData?.fields || [];
            const field = Array.isArray(nodeFields)
              ? nodeFields.find(
                  (f) =>
                    (
                      f.field_name ||
                      f.name ||
                      f.fieldName ||
                      ''
                    ).toLowerCase() === String(tField).toLowerCase(),
                )
              : null;
            if (field) {
              tFieldType =
                String(
                  field.data_type ||
                    field.type ||
                    field.dataType ||
                    field.fieldType ||
                    field.datatype ||
                    '',
                ).toLowerCase() || null;
            }
          }
        }

        // Skip entities with no configuration (all fields are null/empty)
        const hasStatusUpdate = sField && sField.trim() !== '';
        const hasIdentifierUpdate = iField && iField.trim() !== '';
        const hasRawResponseField = rField && rField.trim() !== '';

        // Only include entity if at least one field is configured
        if (!hasStatusUpdate && !hasIdentifierUpdate && !hasRawResponseField) {
          return; // Skip this entity - no configuration
        }

        const backtrackEntity = {
          entity_name,
          label,
          status_update: {
            field: sField,
            datatype: sFieldType,
            success_value: successValue,
            failure_value: failureValue,
          },
          identifier_update: {
            field: iField,
            datatype: iFieldType,
          },
          raw_response_field: rField
            ? {
                field: rField,
                datatype: rFieldType,
              }
            : null,
          primary_key: tField
            ? {
                field: tField,
                datatype: tFieldType,
              }
            : null,
          source: sourceInfo || null,
        };

        backtrack_entities.push(backtrackEntity);
      });
    });

    // SINGLE copy of config: write only to node.config
    const baseConfig = selectedNode?.config || {};

    const newConfig = {
      ...baseConfig,
      id: selectedNode.id,
      type: selectedNode.type,
      label: selectedNode?.data?.label || selectedNode?.data?.config?.label,
      backtrack_entities,
      // entity_field_selections: entitySelections,  // if you still need this
    };

    // updater will put this on node.config and strip data.config
    updateNodeData(selectedNode.id, newConfig);

    onConfigUpdate?.(newConfig);
    setSelectedNode(null);
  };

  if (!selectedNode) return null;

  return (
    <Box
      sx={{
        maxWidth: '680px',
        backgroundColor: darkMode ? '#1e1e1e' : theme.palette.background.paper,
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        color: darkMode ? '#ffffff' : theme.palette.text.primary,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ fontSize: 19 }}>
          Configure Backtrack
        </Typography>
      </Box>

      {/* Configuration Sections */}
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {selectedSourceEntities.length === 0 && (
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            No source entities selected in this workflow. Please select entities
            in your Field Mapping step first.
          </Typography>
        )}

        {/* Render visible source blocks */}
        {Object.entries(entitiesByConnection)
          .filter(([connectionKey]) => !hiddenSourceBlocks.has(connectionKey))
          .map(([connectionKey, connectionData]) => {
            const connectionName = connectionData.connectionName;
            const entities = connectionData.entities;
            const hasMultipleConnections =
              Object.keys(entitiesByConnection).length > 1;

            return (
              <Box
                key={connectionKey}
                sx={{
                  border: darkMode ? '1px solid #303030' : '1px solid #E6E8EB',
                  borderRadius: '12px',
                  p: 2,
                  position: 'relative',
                }}
              >
                {/* Delete button for source block (only when multiple sources) */}
                {hasMultipleConnections && (
                  <Tooltip
                    title={`Delete ${connectionName} configuration block and all its entity configurations`}
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Are you sure you want to remove the configuration block for "${connectionName}"? All configurations for this source will be deleted.`,
                          )
                        ) {
                          removeSourceBlock(connectionKey);
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: darkMode
                          ? 'rgba(90, 102, 166, 0.6)'
                          : 'rgba(90, 102, 166, 0.5)',
                        '&:hover': {
                          color: '#ff6b6b',
                          backgroundColor: darkMode
                            ? 'rgba(255, 107, 107, 0.15)'
                            : 'rgba(255, 107, 107, 0.1)',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {/* Connection Selection */}
                {hasMultipleConnections ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5, fontWeight: 500 }}
                    >
                      Source Connection
                    </Typography>
                    <Select
                      fullWidth
                      value={connectionKey}
                      sx={getDropdownStyle()}
                      MenuProps={MENU_PROPS}
                      IconComponent={KeyboardArrowDownIcon}
                      disabled
                    >
                      {Object.entries(entitiesByConnection).map(
                        ([key, data]) => (
                          <MenuItem key={key} value={key}>
                            {data.connectionName}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                    {/* Configured Entities Chips */}
                    {(() => {
                      const configuredEntities = getConfiguredEntities(
                        connectionKey,
                        entities,
                      );
                      if (configuredEntities.length === 0) return null;

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 1.5,
                            mt: 1,
                          }}
                        >
                          {configuredEntities.map((entity) => {
                            const entityKey = `${entity.compositeKey}__${entity.entity_name}`;
                            // Find the configId for this entity
                            const configId =
                              configIds.find((id) => {
                                const config = entitySelections[id];
                                return (
                                  config?.entityKey === entityKey &&
                                  config?.connectionKey === connectionKey
                                );
                              }) || entityKey;

                            return (
                              <Chip
                                key={entityKey}
                                label={entity.label || entity.entity_name}
                                onDelete={() => {
                                  // Remove configuration for this entity
                                  removeConfiguration(configId);
                                  // Also remove from selectedEntityPerConnection if it's selected
                                  if (
                                    selectedEntityPerConnection[
                                      connectionKey
                                    ] === entityKey
                                  ) {
                                    setSelectedEntityPerConnection((prev) => {
                                      const updated = { ...prev };
                                      delete updated[connectionKey];
                                      return updated;
                                    });
                                  }
                                }}
                                deleteIcon={
                                  <Tooltip
                                    title={`Delete configuration for ${entity.label || entity.entity_name}`}
                                    arrow
                                    placement="top"
                                  >
                                    <DeleteIcon
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        color: darkMode
                                          ? 'rgba(90, 102, 166, 0.6)'
                                          : 'rgba(90, 102, 166, 0.5)',
                                      }}
                                    />
                                  </Tooltip>
                                }
                                sx={{
                                  backgroundColor: darkMode
                                    ? 'rgba(90, 102, 166, 0.2)'
                                    : 'rgba(90, 102, 166, 0.1)',
                                  color: darkMode ? '#ffffff' : '#5A66A6',
                                  fontSize: '0.75rem',
                                  height: '24px',
                                  '& .MuiChip-deleteIcon': {
                                    fontSize: '0.97rem',
                                    color: darkMode
                                      ? 'rgba(90, 102, 166, 0.6)'
                                      : 'rgba(90, 102, 166, 0.5)',
                                    '&:hover': {
                                      color: '#ff6b6b',
                                      backgroundColor: darkMode
                                        ? 'rgba(255, 107, 107, 0.15)'
                                        : 'rgba(255, 107, 107, 0.1)',
                                    },
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <Typography variant="caption" sx={{ opacity: 0.7, mb: 1 }}>
                      Source: {connectionName}
                    </Typography>
                    {/* Configured Entities Chips */}
                    {(() => {
                      const configuredEntities = getConfiguredEntities(
                        connectionKey,
                        entities,
                      );
                      if (configuredEntities.length === 0) return null;

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 1.5,
                            mt: 0.5,
                          }}
                        >
                          {configuredEntities.map((entity) => {
                            const entityKey = `${entity.compositeKey}__${entity.entity_name}`;
                            // Find the configId for this entity
                            const configId =
                              configIds.find((id) => {
                                const config = entitySelections[id];
                                return (
                                  config?.entityKey === entityKey &&
                                  config?.connectionKey === connectionKey
                                );
                              }) || entityKey;

                            return (
                              <Chip
                                key={entityKey}
                                label={entity.label || entity.entity_name}
                                onDelete={() => {
                                  // Remove configuration for this entity
                                  removeConfiguration(configId);
                                  // Also remove from selectedEntityPerConnection if it's selected
                                  if (
                                    selectedEntityPerConnection[
                                      connectionKey
                                    ] === entityKey
                                  ) {
                                    setSelectedEntityPerConnection((prev) => {
                                      const updated = { ...prev };
                                      delete updated[connectionKey];
                                      return updated;
                                    });
                                  }
                                }}
                                deleteIcon={
                                  <Tooltip
                                    title={`Delete configuration for ${entity.label || entity.entity_name}`}
                                    arrow
                                    placement="top"
                                  >
                                    <DeleteIcon
                                      sx={{
                                        fontSize: '0.65rem',
                                        color: darkMode
                                          ? 'rgba(90, 102, 166, 0.6)'
                                          : 'rgba(90, 102, 166, 0.5)',
                                      }}
                                    />
                                  </Tooltip>
                                }
                                sx={{
                                  backgroundColor: darkMode
                                    ? 'rgba(90, 102, 166, 0.2)'
                                    : 'rgba(90, 102, 166, 0.1)',
                                  color: darkMode ? '#ffffff' : '#5A66A6',
                                  fontSize: '0.75rem',
                                  height: '24px',
                                  '& .MuiChip-deleteIcon': {
                                    color: darkMode
                                      ? 'rgba(90, 102, 166, 0.6)'
                                      : 'rgba(90, 102, 166, 0.5)',
                                    '&:hover': {
                                      color: '#ff6b6b',
                                      backgroundColor: darkMode
                                        ? 'rgba(255, 107, 107, 0.15)'
                                        : 'rgba(255, 107, 107, 0.1)',
                                    },
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>
                      );
                    })()}
                  </>
                )}

                {/* Entity Selection */}
                {entities.length > 1 ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5, mt: 1, fontWeight: 500 }}
                    >
                      Entity
                    </Typography>
                    <Select
                      fullWidth
                      value={selectedEntityPerConnection[connectionKey] || ''}
                      onChange={(e) =>
                        setSelectedEntityPerConnection((prev) => ({
                          ...prev,
                          [connectionKey]: e.target.value,
                        }))
                      }
                      displayEmpty
                      sx={getDropdownStyle()}
                      MenuProps={MENU_PROPS}
                      IconComponent={KeyboardArrowDownIcon}
                    >
                      <MenuItem value="" disabled>
                        Select an entity
                      </MenuItem>
                      {entities.map((entity) => {
                        const entityKey = `${entity.compositeKey}__${entity.entity_name}`;
                        return (
                          <MenuItem key={entityKey} value={entityKey}>
                            {entity.label || entity.entity_name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </>
                ) : entities.length === 1 ? (
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1.5, mt: 1, fontWeight: 600 }}
                  >
                    {entities[0].label || entities[0].entity_name}
                  </Typography>
                ) : null}

                {/* Configuration for selected entity */}
                {(() => {
                  const selectedEntityKey =
                    selectedEntityPerConnection[connectionKey] ||
                    (entities.length === 1
                      ? `${entities[0].compositeKey}__${entities[0].entity_name}`
                      : null);

                  if (!selectedEntityKey) return null;

                  // Use key to force re-render when selectedEntityKey changes
                  const configSectionKey = `config-${connectionKey}-${selectedEntityKey}`;

                  const selectedEntity = entities.find(
                    (e) =>
                      `${e.compositeKey}__${e.entity_name}` ===
                      selectedEntityKey,
                  );

                  if (!selectedEntity) return null;

                  const normalizedEntityName = String(
                    selectedEntity.entity_name,
                  ).toLowerCase();
                  const fieldOptions = (
                    fieldsByEntity?.[normalizedEntityName] || []
                  ).map((f) => ({
                    value: f.name,
                    name: f.name,
                    type: f?.type ? String(f.type).toLowerCase() : '',
                    is_primary_key: f?.is_primary_key || false,
                  }));
                  // Filter for primary key fields only
                  const primaryKeyFieldOptions = fieldOptions.filter(
                    (f) => f.is_primary_key === true,
                  );

                  const configsForEntity = configIds.filter((id) => {
                    // Match if configId starts with selectedEntityKey (for old format) or if entityKey matches
                    if (id.startsWith(selectedEntityKey)) return true;
                    // Check if the config has this entityKey
                    const config = entitySelections[id];
                    return config?.entityKey === selectedEntityKey;
                  });

                  const configsToShow =
                    entities.length > 1
                      ? configsForEntity.slice(0, 1) // Show only first config
                      : configsForEntity; // Show all configs for single entity

                  // If no config exists, show empty form that will create config on first interaction
                  if (configsToShow.length === 0) {
                    const emptyConfig = {
                      connectionKey,
                      entityKey: selectedEntityKey,
                      status_update: '',
                      success_value: '',
                      failure_value: '',
                      raw_response_field: '',
                      identifier_update: '',
                      primary_key: '',
                    };
                    const currentConfig =
                      entitySelections[selectedEntityKey] || emptyConfig;
                    const hasStatusField = !!currentConfig.status_update;

                    return (
                      <Box
                        key={configSectionKey}
                        sx={{ mt: 2 }}
                        data-entity-key={selectedEntityKey}
                      >
                        {/* Status Field Dropdown */}
                        <Typography
                          variant="body2"
                          sx={{ mb: 0.5, fontWeight: 500 }}
                        >
                          Status update
                        </Typography>
                        <Select
                          fullWidth
                          value={currentConfig.status_update || ''}
                          onChange={(e) => {
                            // Create config on first selection
                            if (!configIds.includes(selectedEntityKey)) {
                              setConfigIds((prev) => [
                                ...prev,
                                selectedEntityKey,
                              ]);
                            }
                            setEntitySelections((prev) => ({
                              ...prev,
                              [selectedEntityKey]: {
                                ...(prev[selectedEntityKey] || emptyConfig),
                                status_update: e.target.value,
                              },
                            }));
                            // Clear error when user selects a value
                            setValidationErrors((prev) => {
                              const updated = { ...prev };
                              if (updated[selectedEntityKey]) {
                                delete updated[selectedEntityKey].status_update;
                                if (
                                  Object.keys(updated[selectedEntityKey])
                                    .length === 0
                                ) {
                                  delete updated[selectedEntityKey];
                                }
                              }
                              return updated;
                            });
                          }}
                          displayEmpty
                          sx={getDropdownStyle(
                            !!validationErrors[selectedEntityKey]
                              ?.status_update,
                          )}
                          MenuProps={MENU_PROPS}
                          IconComponent={KeyboardArrowDownIcon}
                          data-config-id={selectedEntityKey}
                          renderValue={(val) => {
                            const opt = fieldOptions.find(
                              (o) => o.value === val,
                            );
                            if (!opt) return 'Select a field';
                            return (
                              <Box
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Typography variant="body2">
                                  {opt.name}
                                </Typography>
                                {opt.type && (
                                  <Typography
                                    variant="caption"
                                    sx={{ ml: 1, color: 'text.secondary' }}
                                  >
                                    ({opt.type})
                                  </Typography>
                                )}
                              </Box>
                            );
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select a field
                          </MenuItem>
                          {fieldOptions.map((opt) => {
                            const isSelected =
                              (currentConfig.status_update || '') === opt.value;
                            return (
                              <MenuItem
                                key={opt.value}
                                value={opt.value}
                                dense
                                role="menuitemcheckbox"
                                aria-checked={isSelected}
                                sx={{ minHeight: 36 }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'baseline',
                                      gap: 1,
                                      flex: '1 1 auto',
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {opt.name}
                                    </Typography>
                                    {opt.type && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary' }}
                                      >
                                        ({opt.type})
                                      </Typography>
                                    )}
                                  </Box>
                                  <CheckIcon
                                    fontSize="small"
                                    sx={{
                                      ml: 1,
                                      opacity: isSelected ? 1 : 0,
                                      transition: 'opacity 120ms ease',
                                    }}
                                  />
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {validationErrors[selectedEntityKey]?.status_update && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#f44336',
                              mt: -1,
                              mb: 1,
                              ml: 1.5,
                              display: 'block',
                            }}
                          >
                            {validationErrors[selectedEntityKey].status_update}
                          </Typography>
                        )}

                        {/* Success and Failure Value Inputs (shown after status field selection) */}
                        {hasStatusField && (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1.5,
                              mb: 1,
                            }}
                          >
                            <TextField
                              sx={{
                                ...getTextFieldStyle(
                                  !!validationErrors[selectedEntityKey]
                                    ?.success_value,
                                ),
                                flex: 1,
                                mb: 0,
                              }}
                              label="Set success value"
                              value={currentConfig.success_value || ''}
                              onChange={(e) => {
                                if (!configIds.includes(selectedEntityKey)) {
                                  setConfigIds((prev) => [
                                    ...prev,
                                    selectedEntityKey,
                                  ]);
                                }
                                setEntitySelections((prev) => ({
                                  ...prev,
                                  [selectedEntityKey]: {
                                    ...(prev[selectedEntityKey] || emptyConfig),
                                    success_value: e.target.value,
                                  },
                                }));
                                // Clear error when user enters a value
                                setValidationErrors((prev) => {
                                  const updated = { ...prev };
                                  if (updated[selectedEntityKey]) {
                                    delete updated[selectedEntityKey]
                                      .success_value;
                                    if (
                                      Object.keys(updated[selectedEntityKey])
                                        .length === 0
                                    ) {
                                      delete updated[selectedEntityKey];
                                    }
                                  }
                                  return updated;
                                });
                              }}
                              variant="outlined"
                              error={
                                !!validationErrors[selectedEntityKey]
                                  ?.success_value
                              }
                              helperText={
                                validationErrors[selectedEntityKey]
                                  ?.success_value || ''
                              }
                            />
                            <TextField
                              sx={{
                                ...getTextFieldStyle(
                                  !!validationErrors[selectedEntityKey]
                                    ?.failure_value,
                                ),
                                flex: 1,
                                mb: 0,
                              }}
                              label="Set failure value"
                              value={currentConfig.failure_value || ''}
                              onChange={(e) => {
                                if (!configIds.includes(selectedEntityKey)) {
                                  setConfigIds((prev) => [
                                    ...prev,
                                    selectedEntityKey,
                                  ]);
                                }
                                setEntitySelections((prev) => ({
                                  ...prev,
                                  [selectedEntityKey]: {
                                    ...(prev[selectedEntityKey] || emptyConfig),
                                    failure_value: e.target.value,
                                  },
                                }));
                                // Clear error when user enters a value
                                setValidationErrors((prev) => {
                                  const updated = { ...prev };
                                  if (updated[selectedEntityKey]) {
                                    delete updated[selectedEntityKey]
                                      .failure_value;
                                    if (
                                      Object.keys(updated[selectedEntityKey])
                                        .length === 0
                                    ) {
                                      delete updated[selectedEntityKey];
                                    }
                                  }
                                  return updated;
                                });
                              }}
                              variant="outlined"
                              error={
                                !!validationErrors[selectedEntityKey]
                                  ?.failure_value
                              }
                              helperText={
                                validationErrors[selectedEntityKey]
                                  ?.failure_value || ''
                              }
                            />
                          </Box>
                        )}

                        {/* Raw Response Field and Identifier Update */}
                        <Box sx={{ mt: hasStatusField ? 1.5 : 1.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Raw response field
                            </Typography>
                            <Tooltip
                              title="Store raw response in the field"
                              arrow
                            >
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  border: `1px solid ${
                                    darkMode ? '#666666' : '#999999'
                                  }`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'help',
                                  fontSize: '0.75rem',
                                  color: darkMode ? '#999999' : '#666666',
                                }}
                              >
                                ?
                              </Box>
                            </Tooltip>
                          </Box>
                          <Select
                            fullWidth
                            value={currentConfig.raw_response_field || ''}
                            onChange={(e) => {
                              if (!configIds.includes(selectedEntityKey)) {
                                setConfigIds((prev) => [
                                  ...prev,
                                  selectedEntityKey,
                                ]);
                              }
                              setEntitySelections((prev) => ({
                                ...prev,
                                [selectedEntityKey]: {
                                  ...(prev[selectedEntityKey] || emptyConfig),
                                  raw_response_field: e.target.value,
                                },
                              }));
                            }}
                            displayEmpty
                            sx={getDropdownStyle()}
                            MenuProps={MENU_PROPS}
                            IconComponent={KeyboardArrowDownIcon}
                            renderValue={(val) => {
                              const opt = fieldOptions.find(
                                (o) => o.value === val,
                              );
                              if (!opt) return 'Select a field';
                              return (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <Typography variant="body2">
                                    {opt.name}
                                  </Typography>
                                  {opt.type && (
                                    <Typography
                                      variant="caption"
                                      sx={{ ml: 1, color: 'text.secondary' }}
                                    >
                                      ({opt.type})
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select a field
                            </MenuItem>
                            {(() => {
                              // Filter fields: only string-like types, exclude status_update field
                              const excludedTypes = [
                                'id',
                                'reference',
                                'integer',
                                'int',
                                'number',
                                'long',
                                'double',
                                'float',
                                'decimal',
                                'boolean',
                                'bool',
                                'date',
                                'datetime',
                                'timestamp',
                                'time',
                              ];
                              const statusUpdateField =
                                currentConfig.status_update || '';
                              return fieldOptions.filter((opt) => {
                                // Exclude if selected in status_update
                                if (opt.value === statusUpdateField) {
                                  return false;
                                }
                                // Exclude if type is in excluded list
                                const fieldType = (
                                  opt.type || ''
                                ).toLowerCase();
                                if (
                                  excludedTypes.some((excluded) =>
                                    fieldType.includes(excluded),
                                  )
                                ) {
                                  return false;
                                }
                                // Include string-like types or if type is empty/undefined (assume string)
                                return (
                                  !fieldType ||
                                  fieldType.includes('string') ||
                                  fieldType.includes('text') ||
                                  fieldType.includes('varchar') ||
                                  fieldType.includes('char')
                                );
                              });
                            })().map((opt) => {
                              const isSelected =
                                (currentConfig.raw_response_field || '') ===
                                opt.value;
                              return (
                                <MenuItem
                                  key={opt.value}
                                  value={opt.value}
                                  dense
                                  role="menuitemcheckbox"
                                  aria-checked={isSelected}
                                  sx={{ minHeight: 36 }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '100%',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 1,
                                        flex: '1 1 auto',
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {opt.name}
                                      </Typography>
                                      {opt.type && (
                                        <Typography
                                          variant="caption"
                                          sx={{ color: 'text.secondary' }}
                                        >
                                          ({opt.type})
                                        </Typography>
                                      )}
                                    </Box>
                                    <CheckIcon
                                      fontSize="small"
                                      sx={{
                                        ml: 1,
                                        opacity: isSelected ? 1 : 0,
                                        transition: 'opacity 120ms ease',
                                      }}
                                    />
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                              mt: 1.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Identifier update
                            </Typography>
                            <Tooltip
                              title="Write destination ID to the source field"
                              arrow
                            >
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  border: `1px solid ${
                                    darkMode ? '#666666' : '#999999'
                                  }`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'help',
                                  fontSize: '0.75rem',
                                  color: darkMode ? '#999999' : '#666666',
                                }}
                              >
                                ?
                              </Box>
                            </Tooltip>
                          </Box>
                          <Select
                            fullWidth
                            value={currentConfig.identifier_update || ''}
                            onChange={(e) => {
                              if (!configIds.includes(selectedEntityKey)) {
                                setConfigIds((prev) => [
                                  ...prev,
                                  selectedEntityKey,
                                ]);
                              }
                              setEntitySelections((prev) => ({
                                ...prev,
                                [selectedEntityKey]: {
                                  ...(prev[selectedEntityKey] || emptyConfig),
                                  identifier_update: e.target.value,
                                },
                              }));
                              // Clear error when user selects a value
                              setValidationErrors((prev) => {
                                const updated = { ...prev };
                                if (updated[selectedEntityKey]) {
                                  delete updated[selectedEntityKey]
                                    .identifier_update;
                                  if (
                                    Object.keys(updated[selectedEntityKey])
                                      .length === 0
                                  ) {
                                    delete updated[selectedEntityKey];
                                  }
                                }
                                return updated;
                              });
                            }}
                            displayEmpty
                            sx={getDropdownStyle(
                              !!validationErrors[selectedEntityKey]
                                ?.identifier_update,
                            )}
                            MenuProps={MENU_PROPS}
                            IconComponent={KeyboardArrowDownIcon}
                            data-config-id={selectedEntityKey}
                            renderValue={(val) => {
                              const opt = fieldOptions.find(
                                (o) => o.value === val,
                              );
                              if (!opt) return 'Select a field';
                              return (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <Typography variant="body2">
                                    {opt.name}
                                  </Typography>
                                  {opt.type && (
                                    <Typography
                                      variant="caption"
                                      sx={{ ml: 1, color: 'text.secondary' }}
                                    >
                                      ({opt.type})
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select a field
                            </MenuItem>
                            {fieldOptions.map((opt) => {
                              const isSelected =
                                (currentConfig.identifier_update || '') ===
                                opt.value;
                              return (
                                <MenuItem
                                  key={opt.value}
                                  value={opt.value}
                                  dense
                                  role="menuitemcheckbox"
                                  aria-checked={isSelected}
                                  sx={{ minHeight: 36 }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '100%',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 1,
                                        flex: '1 1 auto',
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {opt.name}
                                      </Typography>
                                      {opt.type && (
                                        <Typography
                                          variant="caption"
                                          sx={{ color: 'text.secondary' }}
                                        >
                                          ({opt.type})
                                        </Typography>
                                      )}
                                    </Box>
                                    <CheckIcon
                                      fontSize="small"
                                      sx={{
                                        ml: 1,
                                        opacity: isSelected ? 1 : 0,
                                        transition: 'opacity 120ms ease',
                                      }}
                                    />
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {validationErrors[selectedEntityKey]
                            ?.identifier_update && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#f44336',
                                mt: -1,
                                mb: 1,
                                ml: 1.5,
                                display: 'block',
                              }}
                            >
                              {
                                validationErrors[selectedEntityKey]
                                  .identifier_update
                              }
                            </Typography>
                          )}

                          {/* Table ID Dropdown - Only for SQL connectors */}
                          {isSqlConnector(connectionData) && (
                            <>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 0.5,
                                  mt: 1.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Primary key
                                </Typography>
                                <Tooltip
                                  title="Please select Primary key of the table"
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: '50%',
                                      border: `1px solid ${
                                        darkMode ? '#666666' : '#999999'
                                      }`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'help',
                                      fontSize: '0.75rem',
                                      color: darkMode ? '#999999' : '#666666',
                                    }}
                                  >
                                    ?
                                  </Box>
                                </Tooltip>
                              </Box>
                              {primaryKeyFieldOptions.length === 0 ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: darkMode
                                      ? 'rgba(255, 255, 255, 0.6)'
                                      : 'rgba(0, 0, 0, 0.6)',
                                    fontStyle: 'italic',
                                    mb: 1,
                                    mt: 0.5,
                                  }}
                                >
                                  Select the table for sql source backtracking
                                  which has primary key
                                </Typography>
                              ) : (
                                <Select
                                  fullWidth
                                  value={currentConfig.primary_key || ''}
                                  onChange={(e) => {
                                    if (
                                      !configIds.includes(selectedEntityKey)
                                    ) {
                                      setConfigIds((prev) => [
                                        ...prev,
                                        selectedEntityKey,
                                      ]);
                                    }
                                    setEntitySelections((prev) => ({
                                      ...prev,
                                      [selectedEntityKey]: {
                                        ...(prev[selectedEntityKey] ||
                                          emptyConfig),
                                        primary_key: e.target.value,
                                      },
                                    }));
                                  }}
                                  displayEmpty
                                  sx={getDropdownStyle()}
                                  MenuProps={MENU_PROPS}
                                  IconComponent={KeyboardArrowDownIcon}
                                  renderValue={(val) => {
                                    const opt = primaryKeyFieldOptions.find(
                                      (o) => o.value === val,
                                    );
                                    if (!opt) return 'Select a field';
                                    return (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {opt.name}
                                        </Typography>
                                        {opt.type && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              ml: 1,
                                              color: 'text.secondary',
                                            }}
                                          >
                                            ({opt.type})
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    Select a field
                                  </MenuItem>
                                  {primaryKeyFieldOptions.map((opt) => {
                                    const isSelected =
                                      (currentConfig.primary_key || '') ===
                                      opt.value;
                                    return (
                                      <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                        dense
                                        role="menuitemcheckbox"
                                        aria-checked={isSelected}
                                        sx={{ minHeight: 36 }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'baseline',
                                              gap: 1,
                                              flex: '1 1 auto',
                                            }}
                                          >
                                            <Typography variant="body2">
                                              {opt.name}
                                            </Typography>
                                            {opt.type && (
                                              <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary' }}
                                              >
                                                ({opt.type})
                                              </Typography>
                                            )}
                                          </Box>
                                          <CheckIcon
                                            fontSize="small"
                                            sx={{
                                              ml: 1,
                                              opacity: isSelected ? 1 : 0,
                                              transition: 'opacity 120ms ease',
                                            }}
                                          />
                                        </Box>
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={configSectionKey}
                      sx={{ mt: 2 }}
                      data-entity-key={selectedEntityKey}
                    >
                      {/* Configurations for this entity */}
                      {configsToShow.map((configId, configIndex) => {
                        const config = entitySelections[configId] || {};
                        const hasStatusFieldForConfig = !!config.status_update;

                        return (
                          <Box
                            key={configId}
                            sx={{
                              mt: configIndex > 0 ? 2 : 0,
                              pt: configIndex > 0 ? 2 : 0,
                              borderTop:
                                configIndex > 0
                                  ? darkMode
                                    ? '1px solid #303030'
                                    : '1px solid #E6E8EB'
                                  : 'none',
                              position: 'relative',
                            }}
                          >
                            {configIndex > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => removeConfiguration(configId)}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 0,
                                  color: darkMode ? '#ffffff' : '#666666',
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}

                            {/* Status Field Dropdown */}
                            <Typography
                              variant="body2"
                              sx={{ mb: 0.5, fontWeight: 500 }}
                            >
                              Status update
                            </Typography>
                            <Select
                              fullWidth
                              value={config.status_update || ''}
                              onChange={(e) => {
                                setFor(
                                  configId,
                                  'status_update',
                                  e.target.value,
                                );
                                // Clear error when user selects a value
                                setValidationErrors((prev) => {
                                  const updated = { ...prev };
                                  if (updated[configId]) {
                                    delete updated[configId].status_update;
                                    if (
                                      Object.keys(updated[configId]).length ===
                                      0
                                    ) {
                                      delete updated[configId];
                                    }
                                  }
                                  return updated;
                                });
                              }}
                              displayEmpty
                              sx={getDropdownStyle(
                                !!validationErrors[configId]?.status_update,
                              )}
                              MenuProps={MENU_PROPS}
                              IconComponent={KeyboardArrowDownIcon}
                              data-config-id={configId}
                              renderValue={(val) => {
                                const opt = fieldOptions.find(
                                  (o) => o.value === val,
                                );
                                if (!opt) return 'Select a field';
                                return (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {opt.name}
                                    </Typography>
                                    {opt.type && (
                                      <Typography
                                        variant="caption"
                                        sx={{ ml: 1, color: 'text.secondary' }}
                                      >
                                        ({opt.type})
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }}
                            >
                              <MenuItem value="" disabled>
                                Select a field
                              </MenuItem>
                              {fieldOptions.map((opt) => {
                                const isSelected =
                                  (config.status_update || '') === opt.value;

                                return (
                                  <MenuItem
                                    key={opt.value}
                                    value={opt.value}
                                    dense
                                    role="menuitemcheckbox"
                                    aria-checked={isSelected}
                                    sx={{ minHeight: 36 }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'baseline',
                                          gap: 1,
                                          flex: '1 1 auto',
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {opt.name}
                                        </Typography>
                                        {opt.type && (
                                          <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                          >
                                            ({opt.type})
                                          </Typography>
                                        )}
                                      </Box>
                                      <CheckIcon
                                        fontSize="small"
                                        sx={{
                                          ml: 1,
                                          opacity: isSelected ? 1 : 0,
                                          transition: 'opacity 120ms ease',
                                        }}
                                      />
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            {validationErrors[configId]?.status_update && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#f44336',
                                  mt: -1,
                                  mb: 1,
                                  ml: 1.5,
                                  display: 'block',
                                }}
                              >
                                {validationErrors[configId].status_update}
                              </Typography>
                            )}

                            {/* Success and Failure Value Inputs (shown after status field selection) */}
                            {hasStatusFieldForConfig && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1.5,
                                  mb: 1,
                                  mt: 2,
                                }}
                              >
                                <TextField
                                  sx={{
                                    ...getTextFieldStyle(
                                      !!validationErrors[configId]
                                        ?.success_value,
                                    ),
                                    flex: 1,
                                    mb: 0,
                                  }}
                                  label="Set success value"
                                  value={config.success_value || ''}
                                  onChange={(e) => {
                                    setFor(
                                      configId,
                                      'success_value',
                                      e.target.value,
                                    );
                                    // Clear error when user enters a value
                                    setValidationErrors((prev) => {
                                      const updated = { ...prev };
                                      if (updated[configId]) {
                                        delete updated[configId].success_value;
                                        if (
                                          Object.keys(updated[configId])
                                            .length === 0
                                        ) {
                                          delete updated[configId];
                                        }
                                      }
                                      return updated;
                                    });
                                  }}
                                  variant="outlined"
                                  error={
                                    !!validationErrors[configId]?.success_value
                                  }
                                  helperText={
                                    validationErrors[configId]?.success_value ||
                                    ''
                                  }
                                />
                                <TextField
                                  sx={{
                                    ...getTextFieldStyle(
                                      !!validationErrors[configId]
                                        ?.failure_value,
                                    ),
                                    flex: 1,
                                    mb: 0,
                                  }}
                                  label="Set failure value"
                                  value={config.failure_value || ''}
                                  onChange={(e) => {
                                    setFor(
                                      configId,
                                      'failure_value',
                                      e.target.value,
                                    );
                                    // Clear error when user enters a value
                                    setValidationErrors((prev) => {
                                      const updated = { ...prev };
                                      if (updated[configId]) {
                                        delete updated[configId].failure_value;
                                        if (
                                          Object.keys(updated[configId])
                                            .length === 0
                                        ) {
                                          delete updated[configId];
                                        }
                                      }
                                      return updated;
                                    });
                                  }}
                                  variant="outlined"
                                  error={
                                    !!validationErrors[configId]?.failure_value
                                  }
                                  helperText={
                                    validationErrors[configId]?.failure_value ||
                                    ''
                                  }
                                />
                              </Box>
                            )}

                            {/* Raw Response Field and Identifier Update Dropdowns */}
                            <Box sx={{ mt: hasStatusFieldForConfig ? 1.5 : 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Raw response field
                                </Typography>
                                <Tooltip
                                  title="Store raw response in the field"
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: '50%',
                                      border: `1px solid ${
                                        darkMode ? '#666666' : '#999999'
                                      }`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'help',
                                      fontSize: '0.75rem',
                                      color: darkMode ? '#999999' : '#666666',
                                    }}
                                  >
                                    ?
                                  </Box>
                                </Tooltip>
                              </Box>
                              <Select
                                fullWidth
                                value={config.raw_response_field || ''}
                                onChange={(e) =>
                                  setFor(
                                    configId,
                                    'raw_response_field',
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                sx={getDropdownStyle()}
                                MenuProps={MENU_PROPS}
                                IconComponent={KeyboardArrowDownIcon}
                                renderValue={(val) => {
                                  const opt = fieldOptions.find(
                                    (o) => o.value === val,
                                  );
                                  if (!opt) return 'Select a field';
                                  return (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {opt.name}
                                      </Typography>
                                      {opt.type && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            ml: 1,
                                            color: 'text.secondary',
                                          }}
                                        >
                                          ({opt.type})
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select a field
                                </MenuItem>
                                {(() => {
                                  // Filter fields: only string-like types, exclude status_update field
                                  const excludedTypes = [
                                    'id',
                                    'reference',
                                    'integer',
                                    'int',
                                    'number',
                                    'long',
                                    'double',
                                    'float',
                                    'decimal',
                                    'boolean',
                                    'bool',
                                    'date',
                                    'datetime',
                                    'timestamp',
                                    'time',
                                  ];
                                  const statusUpdateField =
                                    config.status_update || '';
                                  return fieldOptions.filter((opt) => {
                                    // Exclude if selected in status_update
                                    if (opt.value === statusUpdateField) {
                                      return false;
                                    }
                                    // Exclude if type is in excluded list
                                    const fieldType = (
                                      opt.type || ''
                                    ).toLowerCase();
                                    if (
                                      excludedTypes.some((excluded) =>
                                        fieldType.includes(excluded),
                                      )
                                    ) {
                                      return false;
                                    }
                                    // Include string-like types or if type is empty/undefined (assume string)
                                    return (
                                      !fieldType ||
                                      fieldType.includes('string') ||
                                      fieldType.includes('text') ||
                                      fieldType.includes('varchar') ||
                                      fieldType.includes('char')
                                    );
                                  });
                                })().map((opt) => {
                                  const isSelected =
                                    (config.raw_response_field || '') ===
                                    opt.value;

                                  return (
                                    <MenuItem
                                      key={opt.value}
                                      value={opt.value}
                                      dense
                                      role="menuitemcheckbox"
                                      aria-checked={isSelected}
                                      sx={{ minHeight: 36 }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          width: '100%',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: 1,
                                            flex: '1 1 auto',
                                          }}
                                        >
                                          <Typography variant="body2">
                                            {opt.name}
                                          </Typography>
                                          {opt.type && (
                                            <Typography
                                              variant="caption"
                                              sx={{ color: 'text.secondary' }}
                                            >
                                              ({opt.type})
                                            </Typography>
                                          )}
                                        </Box>
                                        <CheckIcon
                                          fontSize="small"
                                          sx={{
                                            ml: 1,
                                            opacity: isSelected ? 1 : 0,
                                            transition: 'opacity 120ms ease',
                                          }}
                                        />
                                      </Box>
                                    </MenuItem>
                                  );
                                })}
                              </Select>

                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 0.5,
                                  mt: 1.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Identifier update
                                </Typography>
                                <Tooltip
                                  title="Write destination ID to the source field"
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: '50%',
                                      border: `1px solid ${
                                        darkMode ? '#666666' : '#999999'
                                      }`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'help',
                                      fontSize: '0.75rem',
                                      color: darkMode ? '#999999' : '#666666',
                                    }}
                                  >
                                    ?
                                  </Box>
                                </Tooltip>
                              </Box>
                              <Select
                                fullWidth
                                value={config.identifier_update || ''}
                                onChange={(e) => {
                                  setFor(
                                    configId,
                                    'identifier_update',
                                    e.target.value,
                                  );
                                  // Clear error when user selects a value
                                  setValidationErrors((prev) => {
                                    const updated = { ...prev };
                                    if (updated[configId]) {
                                      delete updated[configId]
                                        .identifier_update;
                                      if (
                                        Object.keys(updated[configId])
                                          .length === 0
                                      ) {
                                        delete updated[configId];
                                      }
                                    }
                                    return updated;
                                  });
                                }}
                                displayEmpty
                                sx={getDropdownStyle(
                                  !!validationErrors[configId]
                                    ?.identifier_update,
                                )}
                                MenuProps={MENU_PROPS}
                                IconComponent={KeyboardArrowDownIcon}
                                data-config-id={configId}
                                renderValue={(val) => {
                                  const opt = fieldOptions.find(
                                    (o) => o.value === val,
                                  );
                                  if (!opt) return 'Select a field';
                                  return (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {opt.name}
                                      </Typography>
                                      {opt.type && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            ml: 1,
                                            color: 'text.secondary',
                                          }}
                                        >
                                          ({opt.type})
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select a field
                                </MenuItem>
                                {fieldOptions.map((opt) => {
                                  const isSelected =
                                    (config.identifier_update || '') ===
                                    opt.value;

                                  return (
                                    <MenuItem
                                      key={opt.value}
                                      value={opt.value}
                                      dense
                                      role="menuitemcheckbox"
                                      aria-checked={isSelected}
                                      sx={{ minHeight: 36 }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          width: '100%',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: 1,
                                            flex: '1 1 auto',
                                          }}
                                        >
                                          <Typography variant="body2">
                                            {opt.name}
                                          </Typography>
                                          {opt.type && (
                                            <Typography
                                              variant="caption"
                                              sx={{ color: 'text.secondary' }}
                                            >
                                              ({opt.type})
                                            </Typography>
                                          )}
                                        </Box>
                                        <CheckIcon
                                          fontSize="small"
                                          sx={{
                                            ml: 1,
                                            opacity: isSelected ? 1 : 0,
                                            transition: 'opacity 120ms ease',
                                          }}
                                        />
                                      </Box>
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                              {validationErrors[configId]
                                ?.identifier_update && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#f44336',
                                    mt: -1,
                                    mb: 1,
                                    ml: 1.5,
                                    display: 'block',
                                  }}
                                >
                                  {validationErrors[configId].identifier_update}
                                </Typography>
                              )}

                              {/* Table ID Dropdown - Only for SQL connectors */}
                              {isSqlConnector(connectionData) && (
                                <>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      mb: 0.5,
                                      mt: 1.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      Primary key
                                    </Typography>
                                    <Tooltip
                                      title="Please select Primary key of the table"
                                      arrow
                                    >
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          borderRadius: '50%',
                                          border: `1px solid ${
                                            darkMode ? '#666666' : '#999999'
                                          }`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'help',
                                          fontSize: '0.75rem',
                                          color: darkMode
                                            ? '#999999'
                                            : '#666666',
                                        }}
                                      >
                                        ?
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                  {primaryKeyFieldOptions.length === 0 ? (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: darkMode
                                          ? 'rgba(255, 255, 255, 0.6)'
                                          : 'rgba(0, 0, 0, 0.6)',
                                        fontStyle: 'italic',
                                        mb: 1,
                                        mt: 0.5,
                                      }}
                                    >
                                      Select the table for sql source
                                      backtracking which has primary key
                                    </Typography>
                                  ) : (
                                    <Select
                                      fullWidth
                                      value={config.primary_key || ''}
                                      onChange={(e) => {
                                        setFor(
                                          configId,
                                          'primary_key',
                                          e.target.value,
                                        );
                                      }}
                                      displayEmpty
                                      sx={getDropdownStyle()}
                                      MenuProps={MENU_PROPS}
                                      IconComponent={KeyboardArrowDownIcon}
                                      renderValue={(val) => {
                                        const opt = primaryKeyFieldOptions.find(
                                          (o) => o.value === val,
                                        );
                                        if (!opt) return 'Select a field';
                                        return (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <Typography variant="body2">
                                              {opt.name}
                                            </Typography>
                                            {opt.type && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  ml: 1,
                                                  color: 'text.secondary',
                                                }}
                                              >
                                                ({opt.type})
                                              </Typography>
                                            )}
                                          </Box>
                                        );
                                      }}
                                    >
                                      <MenuItem value="" disabled>
                                        Select a field
                                      </MenuItem>
                                      {primaryKeyFieldOptions.map((opt) => {
                                        const isSelected =
                                          (config.primary_key || '') ===
                                          opt.value;
                                        return (
                                          <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                            dense
                                            role="menuitemcheckbox"
                                            aria-checked={isSelected}
                                            sx={{ minHeight: 36 }}
                                          >
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'baseline',
                                                  gap: 1,
                                                  flex: '1 1 auto',
                                                }}
                                              >
                                                <Typography variant="body2">
                                                  {opt.name}
                                                </Typography>
                                                {opt.type && (
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: 'text.secondary',
                                                    }}
                                                  >
                                                    ({opt.type})
                                                  </Typography>
                                                )}
                                              </Box>
                                              <CheckIcon
                                                fontSize="small"
                                                sx={{
                                                  ml: 1,
                                                  opacity: isSelected ? 1 : 0,
                                                  transition:
                                                    'opacity 120ms ease',
                                                }}
                                              />
                                            </Box>
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                  )}
                                </>
                              )}
                            </Box>
                          </Box>
                        );
                      })}

                      {/* New Configuration Block - appears when adding a new configuration */}
                      {newConfigBlocks[connectionKey] &&
                        (() => {
                          const newBlock = newConfigBlocks[connectionKey];
                          const newConfigId = newBlock.configId;
                          const currentConfig = entitySelections[
                            newConfigId
                          ] || {
                            connectionKey,
                            entityKey: newBlock.entityKey,
                            status_update: '',
                            success_value: '',
                            failure_value: '',
                            raw_response_field: '',
                            identifier_update: '',
                            primary_key: '',
                          };

                          // Get the selected entity for this new block
                          const selectedEntityKey =
                            currentConfig.entityKey || newBlock.entityKey;
                          const selectedEntity = entities.find(
                            (e) =>
                              `${e.compositeKey}__${e.entity_name}` ===
                              selectedEntityKey,
                          );

                          if (!selectedEntity) return null;

                          // Get unconfigured entities, but include the currently selected entity in the new block
                          const unconfiguredEntities = getUnconfiguredEntities(
                            connectionKey,
                            entities,
                          );
                          // Ensure the currently selected entity is in the list
                          const availableEntities = unconfiguredEntities.some(
                            (e) =>
                              `${e.compositeKey}__${e.entity_name}` ===
                              selectedEntityKey,
                          )
                            ? unconfiguredEntities
                            : [...unconfiguredEntities, selectedEntity];

                          const normalizedEntityName = String(
                            selectedEntity.entity_name,
                          ).toLowerCase();
                          const fieldOptions = (
                            fieldsByEntity?.[normalizedEntityName] || []
                          ).map((f) => ({
                            value: f.name,
                            name: f.name,
                            type: f?.type ? String(f.type).toLowerCase() : '',
                            is_primary_key: f?.is_primary_key || false,
                          }));
                          // Filter for primary key fields only
                          const primaryKeyFieldOptions = fieldOptions.filter(
                            (f) => f.is_primary_key === true,
                          );

                          const hasStatusField = !!currentConfig.status_update;

                          return (
                            <Box
                              key={newConfigId}
                              data-new-config-id={newConfigId}
                              sx={{
                                mt: 3,
                                pt: 3,
                                borderTop: darkMode
                                  ? '1px solid #303030'
                                  : '1px solid #E6E8EB',
                              }}
                            >
                              {/* Source Name */}
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  mb: 1.5,
                                  fontWeight: 600,
                                  color: darkMode ? '#ffffff' : '#333333',
                                }}
                              >
                                Source: {connectionName}
                              </Typography>

                              {/* Configured Entities Chips */}
                              {(() => {
                                const configuredEntities =
                                  getConfiguredEntities(
                                    connectionKey,
                                    entities,
                                  );
                                if (configuredEntities.length === 0)
                                  return null;

                                return (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 1,
                                      mb: 1.5,
                                    }}
                                  >
                                    {configuredEntities.map((entity) => {
                                      const entityKey = `${entity.compositeKey}__${entity.entity_name}`;
                                      // Find the configId for this entity
                                      const configId =
                                        configIds.find((id) => {
                                          const config = entitySelections[id];
                                          return (
                                            config?.entityKey === entityKey &&
                                            config?.connectionKey ===
                                              connectionKey
                                          );
                                        }) || entityKey;

                                      return (
                                        <Chip
                                          key={entityKey}
                                          label={
                                            entity.label || entity.entity_name
                                          }
                                          onDelete={() => {
                                            // Remove configuration for this entity
                                            removeConfiguration(configId);
                                            // Also remove from newConfigBlocks if it's there
                                            if (
                                              newConfigBlocks[connectionKey]
                                                ?.configId === configId
                                            ) {
                                              setNewConfigBlocks((prev) => {
                                                const updated = { ...prev };
                                                delete updated[connectionKey];
                                                return updated;
                                              });
                                            }
                                          }}
                                          deleteIcon={
                                            <Tooltip
                                              title={`Delete configuration for ${entity.label || entity.entity_name}`}
                                              arrow
                                              placement="top"
                                            >
                                              <DeleteIcon
                                                sx={{
                                                  fontSize: '0.65rem',
                                                  color: darkMode
                                                    ? 'rgba(90, 102, 166, 0.6)'
                                                    : 'rgba(90, 102, 166, 0.5)',
                                                }}
                                              />
                                            </Tooltip>
                                          }
                                          sx={{
                                            backgroundColor: darkMode
                                              ? 'rgba(90, 102, 166, 0.2)'
                                              : 'rgba(90, 102, 166, 0.1)',
                                            color: darkMode
                                              ? '#ffffff'
                                              : '#5A66A6',
                                            fontSize: '0.75rem',
                                            height: '24px',
                                            '& .MuiChip-deleteIcon': {
                                              color: darkMode
                                                ? 'rgba(90, 102, 166, 0.6)'
                                                : 'rgba(90, 102, 166, 0.5)',
                                              '&:hover': {
                                                color: '#ff6b6b',
                                                backgroundColor: darkMode
                                                  ? 'rgba(255, 107, 107, 0.15)'
                                                  : 'rgba(255, 107, 107, 0.1)',
                                              },
                                            },
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                );
                              })()}

                              {/* Entity Selection Dropdown */}
                              <Typography
                                variant="body2"
                                sx={{ mb: 0.5, mt: 1, fontWeight: 500 }}
                              >
                                Entity
                              </Typography>
                              <Select
                                fullWidth
                                value={selectedEntityKey}
                                onChange={(e) => {
                                  const newEntityKey = e.target.value;
                                  setEntitySelections((prev) => ({
                                    ...prev,
                                    [newConfigId]: {
                                      ...(prev[newConfigId] || {}),
                                      entityKey: newEntityKey,
                                      // Reset fields when entity changes
                                      status_update: '',
                                      success_value: '',
                                      failure_value: '',
                                      raw_response_field: '',
                                      identifier_update: '',
                                      primary_key: '',
                                    },
                                  }));
                                  // Update newConfigBlocks
                                  setNewConfigBlocks((prev) => ({
                                    ...prev,
                                    [connectionKey]: {
                                      ...prev[connectionKey],
                                      entityKey: newEntityKey,
                                    },
                                  }));
                                }}
                                displayEmpty
                                sx={getDropdownStyle()}
                                MenuProps={MENU_PROPS}
                                IconComponent={KeyboardArrowDownIcon}
                              >
                                <MenuItem value="" disabled>
                                  Select an entity
                                </MenuItem>
                                {availableEntities.map((entity) => {
                                  const entityKey = `${entity.compositeKey}__${entity.entity_name}`;
                                  return (
                                    <MenuItem key={entityKey} value={entityKey}>
                                      {entity.label || entity.entity_name}
                                    </MenuItem>
                                  );
                                })}
                              </Select>

                              {/* Status Field Dropdown */}
                              <Typography
                                variant="body2"
                                sx={{ mb: 0.5, mt: 2, fontWeight: 500 }}
                              >
                                Status update
                              </Typography>
                              <Select
                                fullWidth
                                value={currentConfig.status_update || ''}
                                onChange={(e) => {
                                  setEntitySelections((prev) => ({
                                    ...prev,
                                    [newConfigId]: {
                                      ...(prev[newConfigId] || currentConfig),
                                      status_update: e.target.value,
                                    },
                                  }));
                                  setValidationErrors((prev) => {
                                    const updated = { ...prev };
                                    if (updated[newConfigId]) {
                                      delete updated[newConfigId].status_update;
                                      if (
                                        Object.keys(updated[newConfigId])
                                          .length === 0
                                      ) {
                                        delete updated[newConfigId];
                                      }
                                    }
                                    return updated;
                                  });
                                }}
                                displayEmpty
                                sx={getDropdownStyle(
                                  !!validationErrors[newConfigId]
                                    ?.status_update,
                                )}
                                MenuProps={MENU_PROPS}
                                IconComponent={KeyboardArrowDownIcon}
                                data-config-id={newConfigId}
                                renderValue={(val) => {
                                  const opt = fieldOptions.find(
                                    (o) => o.value === val,
                                  );
                                  if (!opt) return 'Select a field';
                                  return (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {opt.name}
                                      </Typography>
                                      {opt.type && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            ml: 1,
                                            color: 'text.secondary',
                                          }}
                                        >
                                          ({opt.type})
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select a field
                                </MenuItem>
                                {fieldOptions.map((opt) => {
                                  const isSelected =
                                    (currentConfig.status_update || '') ===
                                    opt.value;
                                  return (
                                    <MenuItem
                                      key={opt.value}
                                      value={opt.value}
                                      dense
                                      role="menuitemcheckbox"
                                      aria-checked={isSelected}
                                      sx={{ minHeight: 36 }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          width: '100%',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: 1,
                                            flex: '1 1 auto',
                                          }}
                                        >
                                          <Typography variant="body2">
                                            {opt.name}
                                          </Typography>
                                          {opt.type && (
                                            <Typography
                                              variant="caption"
                                              sx={{ color: 'text.secondary' }}
                                            >
                                              ({opt.type})
                                            </Typography>
                                          )}
                                        </Box>
                                        <CheckIcon
                                          fontSize="small"
                                          sx={{
                                            ml: 1,
                                            opacity: isSelected ? 1 : 0,
                                            transition: 'opacity 120ms ease',
                                          }}
                                        />
                                      </Box>
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                              {validationErrors[newConfigId]?.status_update && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#f44336',
                                    mt: -1,
                                    mb: 1,
                                    ml: 1.5,
                                    display: 'block',
                                  }}
                                >
                                  {validationErrors[newConfigId].status_update}
                                </Typography>
                              )}

                              {/* Success and Failure Value Inputs */}
                              {hasStatusField && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    mb: 1,
                                    mt: 1.5,
                                  }}
                                >
                                  <TextField
                                    sx={{
                                      ...getTextFieldStyle(
                                        !!validationErrors[newConfigId]
                                          ?.success_value,
                                      ),
                                      flex: 1,
                                      mb: 0,
                                    }}
                                    label="Set success value"
                                    value={currentConfig.success_value || ''}
                                    onChange={(e) => {
                                      setEntitySelections((prev) => ({
                                        ...prev,
                                        [newConfigId]: {
                                          ...(prev[newConfigId] ||
                                            currentConfig),
                                          success_value: e.target.value,
                                        },
                                      }));
                                      setValidationErrors((prev) => {
                                        const updated = { ...prev };
                                        if (updated[newConfigId]) {
                                          delete updated[newConfigId]
                                            .success_value;
                                          if (
                                            Object.keys(updated[newConfigId])
                                              .length === 0
                                          ) {
                                            delete updated[newConfigId];
                                          }
                                        }
                                        return updated;
                                      });
                                    }}
                                    variant="outlined"
                                    error={
                                      !!validationErrors[newConfigId]
                                        ?.success_value
                                    }
                                    helperText={
                                      validationErrors[newConfigId]
                                        ?.success_value || ''
                                    }
                                  />
                                  <TextField
                                    sx={{
                                      ...getTextFieldStyle(
                                        !!validationErrors[newConfigId]
                                          ?.failure_value,
                                      ),
                                      flex: 1,
                                      mb: 0,
                                    }}
                                    label="Set failure value"
                                    value={currentConfig.failure_value || ''}
                                    onChange={(e) => {
                                      setEntitySelections((prev) => ({
                                        ...prev,
                                        [newConfigId]: {
                                          ...(prev[newConfigId] ||
                                            currentConfig),
                                          failure_value: e.target.value,
                                        },
                                      }));
                                      setValidationErrors((prev) => {
                                        const updated = { ...prev };
                                        if (updated[newConfigId]) {
                                          delete updated[newConfigId]
                                            .failure_value;
                                          if (
                                            Object.keys(updated[newConfigId])
                                              .length === 0
                                          ) {
                                            delete updated[newConfigId];
                                          }
                                        }
                                        return updated;
                                      });
                                    }}
                                    variant="outlined"
                                    error={
                                      !!validationErrors[newConfigId]
                                        ?.failure_value
                                    }
                                    helperText={
                                      validationErrors[newConfigId]
                                        ?.failure_value || ''
                                    }
                                  />
                                </Box>
                              )}

                              {/* Raw Response Field and Identifier Update */}
                              <Box sx={{ mt: hasStatusField ? 1.5 : 1.5 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    Raw response field
                                  </Typography>
                                  <Tooltip
                                    title="Store raw response in the field"
                                    arrow
                                  >
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        border: `1px solid ${
                                          darkMode ? '#666666' : '#999999'
                                        }`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'help',
                                        fontSize: '0.75rem',
                                        color: darkMode ? '#999999' : '#666666',
                                      }}
                                    >
                                      ?
                                    </Box>
                                  </Tooltip>
                                </Box>
                                <Select
                                  fullWidth
                                  value={currentConfig.raw_response_field || ''}
                                  onChange={(e) => {
                                    setEntitySelections((prev) => ({
                                      ...prev,
                                      [newConfigId]: {
                                        ...(prev[newConfigId] || currentConfig),
                                        raw_response_field: e.target.value,
                                      },
                                    }));
                                  }}
                                  displayEmpty
                                  sx={getDropdownStyle()}
                                  MenuProps={MENU_PROPS}
                                  IconComponent={KeyboardArrowDownIcon}
                                  renderValue={(val) => {
                                    const opt = fieldOptions.find(
                                      (o) => o.value === val,
                                    );
                                    if (!opt) return 'Select a field';
                                    return (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {opt.name}
                                        </Typography>
                                        {opt.type && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              ml: 1,
                                              color: 'text.secondary',
                                            }}
                                          >
                                            ({opt.type})
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    Select a field
                                  </MenuItem>
                                  {(() => {
                                    const excludedTypes = [
                                      'id',
                                      'reference',
                                      'integer',
                                      'int',
                                      'number',
                                      'long',
                                      'double',
                                      'float',
                                      'decimal',
                                      'boolean',
                                      'bool',
                                      'date',
                                      'datetime',
                                      'timestamp',
                                      'time',
                                    ];
                                    const statusUpdateField =
                                      currentConfig.status_update || '';
                                    return fieldOptions
                                      .filter((opt) => {
                                        if (opt.value === statusUpdateField) {
                                          return false;
                                        }
                                        const fieldType = (
                                          opt.type || ''
                                        ).toLowerCase();
                                        if (
                                          excludedTypes.some((excluded) =>
                                            fieldType.includes(excluded),
                                          )
                                        ) {
                                          return false;
                                        }
                                        return (
                                          !fieldType ||
                                          fieldType.includes('string') ||
                                          fieldType.includes('text') ||
                                          fieldType.includes('varchar') ||
                                          fieldType.includes('char')
                                        );
                                      })
                                      .map((opt) => {
                                        const isSelected =
                                          (currentConfig.raw_response_field ||
                                            '') === opt.value;
                                        return (
                                          <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                            dense
                                            role="menuitemcheckbox"
                                            aria-checked={isSelected}
                                            sx={{ minHeight: 36 }}
                                          >
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'baseline',
                                                  gap: 1,
                                                  flex: '1 1 auto',
                                                }}
                                              >
                                                <Typography variant="body2">
                                                  {opt.name}
                                                </Typography>
                                                {opt.type && (
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: 'text.secondary',
                                                    }}
                                                  >
                                                    ({opt.type})
                                                  </Typography>
                                                )}
                                              </Box>
                                              <CheckIcon
                                                fontSize="small"
                                                sx={{
                                                  ml: 1,
                                                  opacity: isSelected ? 1 : 0,
                                                  transition:
                                                    'opacity 120ms ease',
                                                }}
                                              />
                                            </Box>
                                          </MenuItem>
                                        );
                                      });
                                  })()}
                                </Select>

                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 0.5,
                                    mt: 1.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    Identifier update
                                  </Typography>
                                  <Tooltip
                                    title="Write destination ID to the source field"
                                    arrow
                                  >
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        border: `1px solid ${
                                          darkMode ? '#666666' : '#999999'
                                        }`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'help',
                                        fontSize: '0.75rem',
                                        color: darkMode ? '#999999' : '#666666',
                                      }}
                                    >
                                      ?
                                    </Box>
                                  </Tooltip>
                                </Box>
                                <Select
                                  fullWidth
                                  value={currentConfig.identifier_update || ''}
                                  onChange={(e) => {
                                    setEntitySelections((prev) => ({
                                      ...prev,
                                      [newConfigId]: {
                                        ...(prev[newConfigId] || currentConfig),
                                        identifier_update: e.target.value,
                                      },
                                    }));
                                    setValidationErrors((prev) => {
                                      const updated = { ...prev };
                                      if (updated[newConfigId]) {
                                        delete updated[newConfigId]
                                          .identifier_update;
                                        if (
                                          Object.keys(updated[newConfigId])
                                            .length === 0
                                        ) {
                                          delete updated[newConfigId];
                                        }
                                      }
                                      return updated;
                                    });
                                  }}
                                  displayEmpty
                                  sx={getDropdownStyle(
                                    !!validationErrors[newConfigId]
                                      ?.identifier_update,
                                  )}
                                  MenuProps={MENU_PROPS}
                                  IconComponent={KeyboardArrowDownIcon}
                                  data-config-id={newConfigId}
                                  renderValue={(val) => {
                                    const opt = fieldOptions.find(
                                      (o) => o.value === val,
                                    );
                                    if (!opt) return 'Select a field';
                                    return (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {opt.name}
                                        </Typography>
                                        {opt.type && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              ml: 1,
                                              color: 'text.secondary',
                                            }}
                                          >
                                            ({opt.type})
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    Select a field
                                  </MenuItem>
                                  {fieldOptions.map((opt) => {
                                    const isSelected =
                                      (currentConfig.identifier_update ||
                                        '') === opt.value;
                                    return (
                                      <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                        dense
                                        role="menuitemcheckbox"
                                        aria-checked={isSelected}
                                        sx={{ minHeight: 36 }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'baseline',
                                              gap: 1,
                                              flex: '1 1 auto',
                                            }}
                                          >
                                            <Typography variant="body2">
                                              {opt.name}
                                            </Typography>
                                            {opt.type && (
                                              <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary' }}
                                              >
                                                ({opt.type})
                                              </Typography>
                                            )}
                                          </Box>
                                          <CheckIcon
                                            fontSize="small"
                                            sx={{
                                              ml: 1,
                                              opacity: isSelected ? 1 : 0,
                                              transition: 'opacity 120ms ease',
                                            }}
                                          />
                                        </Box>
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                                {validationErrors[newConfigId]
                                  ?.identifier_update && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#f44336',
                                      mt: -1,
                                      mb: 1,
                                      ml: 1.5,
                                      display: 'block',
                                    }}
                                  >
                                    {
                                      validationErrors[newConfigId]
                                        .identifier_update
                                    }
                                  </Typography>
                                )}

                                {/* Table ID Dropdown - Only for SQL connectors */}
                                {isSqlConnector(connectionData) && (
                                  <>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 0.5,
                                        mt: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        Primary key
                                      </Typography>
                                      <Tooltip
                                        title="Please select Primary key of the table"
                                        arrow
                                      >
                                        <Box
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: '50%',
                                            border: `1px solid ${
                                              darkMode ? '#666666' : '#999999'
                                            }`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'help',
                                            fontSize: '0.75rem',
                                            color: darkMode
                                              ? '#999999'
                                              : '#666666',
                                          }}
                                        >
                                          ?
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                    {primaryKeyFieldOptions.length === 0 ? (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: darkMode
                                            ? 'rgba(255, 255, 255, 0.6)'
                                            : 'rgba(0, 0, 0, 0.6)',
                                          fontStyle: 'italic',
                                          mb: 1,
                                          mt: 0.5,
                                        }}
                                      >
                                        Select the table for sql source
                                        backtracking which has primary key
                                      </Typography>
                                    ) : (
                                      <Select
                                        fullWidth
                                        value={currentConfig.primary_key || ''}
                                        onChange={(e) => {
                                          setEntitySelections((prev) => ({
                                            ...prev,
                                            [newConfigId]: {
                                              ...(prev[newConfigId] ||
                                                currentConfig),
                                              primary_key: e.target.value,
                                            },
                                          }));
                                        }}
                                        displayEmpty
                                        sx={getDropdownStyle()}
                                        MenuProps={MENU_PROPS}
                                        IconComponent={KeyboardArrowDownIcon}
                                        renderValue={(val) => {
                                          const opt =
                                            primaryKeyFieldOptions.find(
                                              (o) => o.value === val,
                                            );
                                          if (!opt) return 'Select a field';
                                          return (
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Typography variant="body2">
                                                {opt.name}
                                              </Typography>
                                              {opt.type && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    ml: 1,
                                                    color: 'text.secondary',
                                                  }}
                                                >
                                                  ({opt.type})
                                                </Typography>
                                              )}
                                            </Box>
                                          );
                                        }}
                                      >
                                        <MenuItem value="" disabled>
                                          Select a field
                                        </MenuItem>
                                        {primaryKeyFieldOptions.map((opt) => {
                                          const isSelected =
                                            (currentConfig.primary_key ||
                                              '') === opt.value;
                                          return (
                                            <MenuItem
                                              key={opt.value}
                                              value={opt.value}
                                              dense
                                              role="menuitemcheckbox"
                                              aria-checked={isSelected}
                                              sx={{ minHeight: 36 }}
                                            >
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  width: '100%',
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    gap: 1,
                                                    flex: '1 1 auto',
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {opt.name}
                                                  </Typography>
                                                  {opt.type && (
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        color: 'text.secondary',
                                                      }}
                                                    >
                                                      ({opt.type})
                                                    </Typography>
                                                  )}
                                                </Box>
                                                <CheckIcon
                                                  fontSize="small"
                                                  sx={{
                                                    ml: 1,
                                                    opacity: isSelected ? 1 : 0,
                                                    transition:
                                                      'opacity 120ms ease',
                                                  }}
                                                />
                                              </Box>
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    )}
                                  </>
                                )}
                              </Box>

                              {/* Delete button for new block */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  mt: 2,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    // Remove from newConfigBlocks
                                    setNewConfigBlocks((prev) => {
                                      const updated = { ...prev };
                                      delete updated[connectionKey];
                                      return updated;
                                    });
                                    // Remove from configIds
                                    setConfigIds((prev) =>
                                      prev.filter((id) => id !== newConfigId),
                                    );
                                    // Remove from entitySelections
                                    setEntitySelections((prev) => {
                                      const updated = { ...prev };
                                      delete updated[newConfigId];
                                      return updated;
                                    });
                                    // Remove validation errors
                                    setValidationErrors((prev) => {
                                      const updated = { ...prev };
                                      delete updated[newConfigId];
                                      return updated;
                                    });
                                  }}
                                  sx={{
                                    color: darkMode ? '#ffffff' : '#666666',
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })()}
                    </Box>
                  );
                })()}
              </Box>
            );
          })}

        {/* Add Source Section - Show deleted sources that can be restored */}
        {hiddenSourceBlocks.size > 0 &&
          Object.keys(entitiesByConnection).length > 1 && (
            <Box
              sx={{
                border: darkMode ? '1px dashed #404040' : '1px dashed #E6E8EB',
                borderRadius: '12px',
                p: 2,
                backgroundColor: darkMode
                  ? 'rgba(90, 102, 166, 0.05)'
                  : 'rgba(90, 102, 166, 0.02)',
              }}
            >
              <Typography
                variant="body2"
                sx={{ mb: 1.5, fontWeight: 500, opacity: 0.8 }}
              >
                Deleted Sources
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {Array.from(hiddenSourceBlocks).map((connectionKey) => {
                  const connectionData = entitiesByConnection[connectionKey];
                  if (!connectionData) return null;
                  const connectionName = connectionData.connectionName;

                  return (
                    <Button
                      key={connectionKey}
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => restoreSourceBlock(connectionKey)}
                      sx={{
                        textTransform: 'none',
                        color: '#5A66A6',
                        borderColor: darkMode ? '#404040' : '#E6E8EB',
                        fontSize: '0.875rem',
                        '&:hover': {
                          borderColor: '#5A66A6',
                          backgroundColor: darkMode
                            ? 'rgba(90, 102, 166, 0.1)'
                            : 'rgba(90, 102, 166, 0.05)',
                        },
                      }}
                    >
                      Add {connectionName}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          )}
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => setSelectedNode(null)}
          sx={{
            borderRadius: '0.5rem',
            padding: '0.3rem 0.8rem',
            fontSize: '0.875rem',
            color: '#5A66A6',
            border: 'none',
            background: 'transparent',
            '&:hover': {
              border: '1px solid #5965A6',
              backgroundColor: darkMode
                ? 'rgba(79, 172, 254, 0.1)'
                : 'rgba(79, 172, 254, 0.05)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          sx={{
            fontSize: '0.875rem',
            backgroundColor: ' #5965A6',
            padding: '0.3rem 0.8rem',
            color: '#ffffff',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            borderRadius: 0.5,
            boxShadow: '0px 4px 10px 0px #0000000D',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.03)',
              backgroundColor: 'rgb(56, 63, 102)',
              boxShadow: 'linear-gradient(90deg, #2f7fd6, #0097d6)',
              border: '1px solid transparent',
            },
            '&:active': {
              backgroundColor: '#1565C0',
              boxShadow: 'linear-gradient(90deg, #2f7fd6, #0097d6)',
              border: '1px solid transparent',
            },
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

BacktrackConfigurationModal.propTypes = {
  selectedNode: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    config: PropTypes.object.isRequired,
    data: PropTypes.shape({
      label: PropTypes.string,
      config: PropTypes.object,
    }).isRequired,
  }),
  setSelectedNode: PropTypes.func.isRequired,
  updateNodeData: PropTypes.func.isRequired,
  onConfigUpdate: PropTypes.func,
  updatedConfig: PropTypes.object,
  workflowId: PropTypes.string.isRequired,
};

export default BacktrackConfigurationModal;
